import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DataGrid, type DataGridColumn } from '@/components/ui/DataGrid'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Input, SearchInput } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import {
  useLeads, useLead, useLeadStats, useCreateLead, useUpdateLead, useQualifyLead, useDeleteLead,
  useCompanies, useCompanyContacts, useLookupCNPJ, useCreateCompanyFromCNPJ,
  useCreateCompany, useUpdateCompany, useSyncCompanyCnpj, useSnovEnrichCompany, useSnovSuggestContacts, useBulkCreateContacts, useCreateContact, useUpdateContact, useDeleteContact, useSetPrimaryContact, useCompany,
  type SnovSuggestion,
  useBulkUpdateLeads, useBulkDeleteLeads,
  useLeadScoreBreakdown, useLeadTimeline,
  useSavedFilters, useUpsertSavedFilter, useDeleteSavedFilter, type SavedFilter,
  useSubOrigins,
} from '@/hooks/useLeads'
import { useCreateOpportunity, useOpportunities, useUpdateActivity, useDeleteActivity } from '@/hooks/useOpportunities'
import { useQueryClient } from '@tanstack/react-query'
import { UserAvatar } from '@/components/ui/Avatar'
import { useTasks } from '@/hooks/useTasks'
import { useUsers } from '@/hooks/useUsers'
import { useAuthStore } from '@/stores/authStore'
import { formatDate, formatDateTime, formatNumber, parseDate } from '@/lib/utils'
import type {
  Lead, LeadCreate, LeadUpdate, LeadQualify, LeadStatus, LeadOrigin,
  Company, Contact, ContactCreate, ContactUpdate, ContactRole,
  OpportunityCreate, ExistingLeadInfo,
} from '@/types'
import { CONTACT_ROLE_LABELS, CHANNEL_CONFIG, type ActivityChannel } from '@/types'
import {
  Plus, Edit2, Trash2, ChevronLeft, ChevronRight, X, AlertTriangle,
  Target, TrendingUp, Users, Award, Eye, CheckCircle, ClipboardCheck,
  Search, Building2, Globe, Database, UserPlus, Star, Linkedin, Phone,
  Mail, MapPin, FileText, Loader2, ClipboardList, Clock, Pencil,
  Save, MessageCircle, StickyNote, ExternalLink,
} from 'lucide-react'
import { CopyButton } from '@/components/ui/CopyButton'
import { TasksPanel } from '@/components/TasksPanel'
import { QualifyBantModal } from '@/components/QualifyBantModal'
import { ContactFormModal } from '@/components/ContactFormModal'

// Helper para extrair mensagem de erro da API
function getApiErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { detail?: string }, status?: number } }
  if (axiosErr?.response?.data?.detail) return axiosErr.response.data.detail
  if (axiosErr?.response?.status === 401) return 'Sessao expirada. Faca login novamente.'
  if (axiosErr?.response?.status === 503) return 'Servico BrasilAPI indisponivel. Tente novamente em instantes.'
  return fallback
}

// ==========================================
// Constants
// ==========================================

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'info' | 'outline' }> = {
  mql: { label: 'Discovery (MQL)', variant: 'default' },
  suspect: { label: 'Suspect', variant: 'secondary' },
  sql: { label: 'Prospect (SQL)', variant: 'warning' },
  customer: { label: 'Cliente', variant: 'success' },
  prospect: { label: 'Prospect', variant: 'info' },
  opportunity: { label: 'Oportunidade', variant: 'success' },
  lost: { label: 'Perdido', variant: 'destructive' },
}

const ACTIVE_STATUSES = ['mql', 'suspect', 'sql', 'customer'] as const

// Temperatura agora deriva exclusivamente do BANT (0-4 dimensões confirmadas).
// 4/4 → Quente · 2-3/4 → Morno · 0-1/4 → Frio
function getTemperature(lead: Pick<Lead, 'bant_score'>): { label: string; color: string } {
  const b = lead.bant_score ?? 0
  if (b >= 4) return { label: 'Quente', color: 'text-green-500' }
  if (b >= 2) return { label: 'Morno', color: 'text-amber-500' }
  return { label: 'Frio', color: 'text-muted-foreground' }
}

const ORIGIN_LABELS: Record<string, string> = {
  event: 'Evento',
  bdr_partners: 'BDR/Parceiro/Indicação',
  active_prospecting: 'Prospecção Ativa',
  website: 'Website',
}

const PRODUCT_OPTIONS = ['ERP', 'Compliance', 'IGA', 'LGPD', 'GRC', 'IAM', 'ESG', 'Governanca', 'Seguranca']

const TAX_ID_TYPES: Record<string, string> = {
  EIN: 'EIN (EUA)',
  VAT: 'VAT (Europa)',
  SIRET: 'SIRET (Franca)',
  NIF: 'NIF (Espanha/Portugal)',
  RFC: 'RFC (Mexico)',
  CUIT: 'CUIT (Argentina)',
  RUT: 'RUT (Chile)',
  NIT: 'NIT (Colombia)',
  ABN: 'ABN (Australia)',
  BN: 'BN (Canada)',
}

const selectClass = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'

// ==========================================
// Main Page
// ==========================================

export function LeadsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState(searchParams.get('search') || '')

  // Reage à URL: quando o usuário pesquisa pela busca global do header (que navega pra /leads?search=…)
  useEffect(() => {
    const fromUrl = searchParams.get('search')
    if (fromUrl !== null && fromUrl !== search) {
      setSearch(fromUrl)
      setPage(1)
      // Limpa o param da URL pra não persistir quando o usuário continuar navegando
      const next = new URLSearchParams(searchParams)
      next.delete('search')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
  const [filterStatus, setFilterStatus] = useState<LeadStatus | ''>('')
  const [filterOrigin, setFilterOrigin] = useState<LeadOrigin | ''>('')
  const [filterSubOrigin, setFilterSubOrigin] = useState<string>('')
  const [filterOwner, setFilterOwner] = useState<string>('')

  // Filtros salvos (persistidos no backend, por usuário)
  const savedFiltersQ = useSavedFilters('leads')
  const upsertSavedFilter = useUpsertSavedFilter()
  const deleteSavedFilterMut = useDeleteSavedFilter()
  const savedFilters: SavedFilter[] = savedFiltersQ.data || []

  const saveCurrentFilter = async () => {
    const name = window.prompt('Nome deste filtro:')?.trim()
    if (!name) return
    try {
      await upsertSavedFilter.mutateAsync({
        scope: 'leads',
        name,
        filters: { search, filterStatus, filterOrigin, filterSubOrigin, filterOwner },
      })
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Erro ao salvar filtro')
    }
  }
  const applySavedFilter = (sf: SavedFilter) => {
    const f = sf.filters || {}
    setSearch(f.search || '')
    setFilterStatus((f.filterStatus || '') as LeadStatus | '')
    setFilterOrigin((f.filterOrigin || '') as LeadOrigin | '')
    setFilterSubOrigin(f.filterSubOrigin || '')
    setFilterOwner(f.filterOwner || '')
  }
  const deleteSavedFilterAction = async (sf: SavedFilter) => {
    if (!window.confirm(`Excluir o filtro "${sf.name}"?`)) return
    try { await deleteSavedFilterMut.mutateAsync(sf.id) }
    catch (e: any) { alert(e?.response?.data?.detail || 'Erro ao excluir filtro') }
  }
  const [showCreate, setShowCreate] = useState(false)
  const [savedMsg, setSavedMsg] = useState<string | null>(null)
  const flashSaved = (msg: string) => {
    setSavedMsg(msg)
    window.setTimeout(() => setSavedMsg(null), 4000)
  }
  const [editLead, setEditLead] = useState<Lead | null>(null)
  const [qualifyLead, setQualifyLead] = useState<Lead | null>(null)
  const [viewLead, setViewLead] = useState<Lead | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<Lead | null>(null)

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkActionMode, setBulkActionMode] = useState<null | 'owner' | 'status'>(null)
  const [bulkValue, setBulkValue] = useState<string>('')
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  const bulkUpdateMutation = useBulkUpdateLeads()
  const bulkDeleteMutation = useBulkDeleteLeads()

  const { data, isLoading } = useLeads({ page: 1, perPage: 500 })
  const { data: stats } = useLeadStats()
  const updateLeadMutation = useUpdateLead()

  const toggleBant = (lead: Lead, field: 'budget_confirmed' | 'authority_confirmed' | 'need_confirmed' | 'timeline_confirmed') => {
    updateLeadMutation.mutate({ leadId: lead.id, data: { [field]: !lead[field] } })
  }

  const filteredLeads = useMemo(() => {
    let items = data?.items || []
    if (search) { const s = search.toLowerCase(); items = items.filter(l => (l.company?.name || '').toLowerCase().includes(s) || (l.company?.trading_name || '').toLowerCase().includes(s) || (l.primary_contact?.display_name || '').toLowerCase().includes(s)) }
    if (filterStatus) items = items.filter(l => l.status === filterStatus)
    if (filterOrigin) items = items.filter(l => l.origin === filterOrigin)
    if (filterSubOrigin) items = items.filter(l => (l.sub_origin || '__none__') === filterSubOrigin)
    if (filterOwner) items = items.filter(l => (l.owner_name || '__none__') === filterOwner)
    return items
  }, [data?.items, search, filterStatus, filterOrigin, filterSubOrigin, filterOwner])

  const uniqueOwners = useMemo(() => {
    const set = new Set<string>()
    ;(data?.items || []).forEach(l => set.add(l.owner_name || '__none__'))
    return Array.from(set).sort((a, b) => a === '__none__' ? 1 : b === '__none__' ? -1 : a.localeCompare(b))
  }, [data?.items])

  const uniqueSubOrigins = useMemo(() => {
    const set = new Set<string>()
    ;(data?.items || []).forEach(l => { if (l.sub_origin) set.add(l.sub_origin) })
    const hasEmpty = (data?.items || []).some(l => !l.sub_origin)
    const arr = Array.from(set).sort((a, b) => a.localeCompare(b))
    if (hasEmpty) arr.push('__none__')
    return arr
  }, [data?.items])

  const allUsers = useUsers({ perPage: 100 }).data?.items || []

  const toggleSelectOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const toggleSelectAllVisible = () => {
    setSelectedIds(prev => {
      const allVisibleSelected = filteredLeads.length > 0 && filteredLeads.every(l => prev.has(l.id))
      if (allVisibleSelected) return new Set()
      return new Set(filteredLeads.map(l => l.id))
    })
  }
  const clearSelection = () => setSelectedIds(new Set())

  const allVisibleSelected = filteredLeads.length > 0 && filteredLeads.every(l => selectedIds.has(l.id))
  const someVisibleSelected = filteredLeads.some(l => selectedIds.has(l.id)) && !allVisibleSelected

  const handleBulkApply = async () => {
    if (!bulkActionMode || !bulkValue) return
    const payload: { lead_ids: string[]; owner_id?: string; status?: string } = { lead_ids: Array.from(selectedIds) }
    if (bulkActionMode === 'owner') payload.owner_id = bulkValue
    if (bulkActionMode === 'status') payload.status = bulkValue
    await bulkUpdateMutation.mutateAsync(payload)
    setBulkActionMode(null)
    setBulkValue('')
    clearSelection()
  }

  const handleBulkDelete = async () => {
    const result = await bulkDeleteMutation.mutateAsync(Array.from(selectedIds))
    setShowBulkDeleteConfirm(false)
    clearSelection()
    if (result.blocked && result.blocked > 0) {
      alert(`${result.affected} lead(s) excluído(s). ${result.blocked} bloqueado(s) por terem oportunidades vinculadas.`)
    }
  }

  return (
    <div className="min-h-screen">
      
      <div className="p-6 space-y-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatCard icon={Users} label="Total" value={stats.total} />
            <StatCard icon={Target} label="Temperatura Média" value={(() => {
              const items = data?.items || []
              if (!items.length) return '—'
              const avgBant = items.reduce((s, l) => s + (l.bant_score || 0), 0) / items.length
              return avgBant >= 3 ? 'Quente' : avgBant >= 1.5 ? 'Morno' : 'Frio'
            })()} />
            <StatCard icon={Award} label="Qualificados" value={stats.qualified_count} />
            <StatCard icon={TrendingUp} label="Convertidos/Mes" value={stats.converted_this_month} />
            <StatCard icon={Users} label="Suspects" value={stats.by_status.suspect || 0} />
            <StatCard icon={CheckCircle} label="MQLs" value={stats.by_status.mql || 0} />
            <StatCard icon={CheckCircle} label="SQLs" value={stats.by_status.sql || 0} />
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 flex-wrap">
            <div className="w-64">
              <SearchInput placeholder="Buscar empresa..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} onClear={() => setSearch('')} />
            </div>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value as LeadStatus | ''); setPage(1) }}>
              <option value="">Todos os status</option>
              {ACTIVE_STATUSES.map((k) => <option key={k} value={k}>{STATUS_CONFIG[k].label}</option>)}
            </select>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterOrigin} onChange={(e) => { setFilterOrigin(e.target.value as LeadOrigin | ''); setPage(1) }}>
              <option value="">Todas as origens</option>
              {Object.entries(ORIGIN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterSubOrigin} onChange={(e) => { setFilterSubOrigin(e.target.value); setPage(1) }}>
              <option value="">Todas as sub-origens</option>
              {uniqueSubOrigins.map((sub) => <option key={sub} value={sub}>{sub === '__none__' ? 'Sem sub-origem' : sub}</option>)}
            </select>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterOwner} onChange={(e) => { setFilterOwner(e.target.value); setPage(1) }}>
              <option value="">Todos os responsáveis</option>
              {uniqueOwners.map((o) => <option key={o} value={o}>{o === '__none__' ? 'Sem responsável' : o}</option>)}
            </select>

            {/* Filtros salvos — dropdown + salvar */}
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value=""
              onChange={(e) => {
                const v = e.target.value
                if (v === '__save__') saveCurrentFilter()
                else if (v.startsWith('__delete__')) {
                  const id = v.replace('__delete__', '')
                  const target = savedFilters.find(x => x.id === id)
                  if (target) deleteSavedFilterAction(target)
                } else if (v) {
                  const sf = savedFilters.find(x => x.id === v)
                  if (sf) applySavedFilter(sf)
                }
                e.currentTarget.selectedIndex = 0
              }}
              title="Filtros salvos (persistidos por usuário)"
              disabled={savedFiltersQ.isLoading}
            >
              <option value="">⭐ Filtros salvos {savedFiltersQ.isLoading ? '(carregando...)' : ''}</option>
              {savedFilters.length > 0 && <optgroup label="Aplicar">
                {savedFilters.map(sf => <option key={sf.id} value={sf.id}>{sf.name}</option>)}
              </optgroup>}
              <optgroup label="Ações">
                <option value="__save__">+ Salvar filtro atual</option>
                {savedFilters.length > 0 && savedFilters.map(sf => (
                  <option key={`del-${sf.id}`} value={`__delete__${sf.id}`}>🗑 Excluir "{sf.name}"</option>
                ))}
              </optgroup>
            </select>
          </div>
          <Button onClick={() => setShowCreate(true)} leftIcon={<Plus className="h-4 w-4" />}>Novo Lead</Button>
        </div>

        {savedMsg && (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 text-sm">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span>{savedMsg}</span>
          </div>
        )}

        {/* Bulk action toolbar — appears when there's a selection */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 p-3 rounded-lg border bg-primary/5 border-primary/30">
            <span className="text-sm font-medium">{selectedIds.size} {selectedIds.size === 1 ? 'lead selecionado' : 'leads selecionados'}</span>
            <div className="h-4 w-px bg-border" />
            <Button size="sm" variant="outline" onClick={() => { setBulkActionMode('owner'); setBulkValue('') }}>
              Alterar responsável
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setBulkActionMode('status'); setBulkValue('') }}>
              Alterar status
            </Button>
            <Button size="sm" variant="outline" className="text-destructive hover:text-destructive border-destructive/40" onClick={() => setShowBulkDeleteConfirm(true)}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Excluir
            </Button>
            <button onClick={clearSelection} className="ml-auto text-xs text-muted-foreground hover:text-foreground underline">Limpar seleção</button>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <DataGrid
              gridId="leads"
              columns={[
                { id: 'select', header: <input type="checkbox" aria-label="Selecionar todos" ref={(el) => { if (el) el.indeterminate = someVisibleSelected }} checked={allVisibleSelected} onChange={toggleSelectAllVisible} className="h-4 w-4 rounded border-input cursor-pointer" onClick={(e: any) => e.stopPropagation()} />, sortable: false, filterable: false, accessor: (lead: Lead) => <input type="checkbox" aria-label="Selecionar lead" checked={selectedIds.has(lead.id)} onChange={() => toggleSelectOne(lead.id)} onClick={(e: any) => e.stopPropagation()} className="h-4 w-4 rounded border-input cursor-pointer" /> },
                { id: 'title', header: 'Nome do Lead', accessor: (lead: Lead) => <button type="button" onClick={(e) => { e.stopPropagation(); setViewLead(lead) }} className="font-medium text-primary hover:underline text-left">{lead.title || '—'}</button>, sortValue: (l) => l.title || '', filterValue: (l) => l.title || '' },
                { id: 'company', header: 'Empresa', accessor: (lead: Lead) => <div><div className="font-medium">{lead.company?.trading_name || lead.company?.name || '-'}</div>{lead.company?.cnpj && <div className="text-xs text-muted-foreground">{lead.company.cnpj}</div>}</div>, sortValue: (l) => l.company?.trading_name || l.company?.name || '', filterValue: (l) => (l.company?.trading_name || '') + ' ' + (l.company?.name || '') + ' ' + (l.company?.cnpj || '') },
                { id: 'contact', header: 'Contato', accessor: (lead: Lead) => <div><div>{lead.primary_contact?.display_name || '-'}</div>{lead.primary_contact?.email && <div className="text-xs text-muted-foreground">{lead.primary_contact.email}</div>}</div>, sortValue: (l) => l.primary_contact?.display_name || '', filterValue: (l) => (l.primary_contact?.display_name || '') + ' ' + (l.primary_contact?.email || '') },
                { id: 'origin', header: 'Origem', accessor: (lead: Lead) => <div className="flex items-center gap-2 flex-wrap"><Badge variant="outline">{ORIGIN_LABELS[lead.origin] || lead.origin}</Badge>{lead.sub_origin && <span className="text-xs text-muted-foreground truncate max-w-[180px]" title={lead.sub_origin}>{lead.sub_origin}</span>}</div>, sortValue: (l) => l.origin, filterValue: (l) => (ORIGIN_LABELS[l.origin] || l.origin) + ' ' + (l.sub_origin || '') },
                { id: 'status', header: 'Status', accessor: (lead: Lead) => <Badge variant={STATUS_CONFIG[lead.status]?.variant || 'secondary'}>{STATUS_CONFIG[lead.status]?.label || lead.status}</Badge>, sortValue: (l) => l.status, filterValue: (l) => STATUS_CONFIG[l.status]?.label || l.status },
                { id: 'bant', header: 'BANT', accessor: (lead: Lead) => { const checks = [lead.budget_confirmed, lead.authority_confirmed, lead.need_confirmed, lead.timeline_confirmed]; return <button type="button" onClick={(e: any) => { e.stopPropagation(); setQualifyLead(lead) }} title="Abrir qualificação BANT completa" className="flex gap-0.5 hover:opacity-80 transition-opacity">{['B','A','N','T'].map((letter, i) => <span key={letter} className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold ${checks[i] ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>{letter}</span>)}</button> }, sortValue: (l) => [l.budget_confirmed, l.authority_confirmed, l.need_confirmed, l.timeline_confirmed].filter(Boolean).length, filterValue: (l) => { const c = [l.budget_confirmed, l.authority_confirmed, l.need_confirmed, l.timeline_confirmed]; return c.filter(Boolean).length + '/4' } },
                { id: 'score', header: 'Temperatura', accessor: (lead: Lead) => { const s = getTemperature(lead); return <span className={`font-semibold ${s.color}`}>{s.label}</span> }, sortValue: (l) => l.bant_score ?? 0, filterValue: (l) => getTemperature(l).label },
                { id: 'products', header: 'Produtos', accessor: (lead: Lead) => <div className="flex flex-wrap gap-1">{(lead.products_interest || []).slice(0,2).map(p => <Badge key={p} variant="outline" className="text-[10px] px-1.5 py-0">{p}</Badge>)}{(lead.products_interest?.length || 0) > 2 && <Badge variant="outline" className="text-[10px] px-1.5 py-0">+{lead.products_interest!.length - 2}</Badge>}</div>, sortValue: (l) => (l.products_interest || []).join(', '), filterValue: (l) => (l.products_interest || []).join(' ') },
                { id: 'owner', header: 'Owner', accessor: (lead: Lead) => <span className="text-muted-foreground text-xs">{lead.owner_name || '-'}</span>, sortValue: (l) => l.owner_name || '', filterValue: (l) => l.owner_name || '' },
                { id: 'created', header: 'Criado', accessor: (lead: Lead) => <span className="text-muted-foreground text-xs">{formatDate(lead.created_at)}</span>, sortValue: (l) => new Date(l.created_at).getTime(), filterValue: (l) => formatDate(l.created_at) },
                { id: 'actions', header: 'Acoes', sortable: false, filterable: false, align: 'right' as const, accessor: (lead: Lead) => <div className="flex justify-end gap-1" onClick={(e: any) => e.stopPropagation()}><Button variant="ghost" size="icon" onClick={() => setViewLead(lead)} title="Ver"><Eye className="h-4 w-4" /></Button><Button variant="ghost" size="icon" onClick={() => setQualifyLead(lead)} title="Qualificar BANT"><ClipboardCheck className="h-4 w-4" /></Button><Button variant="ghost" size="icon" onClick={() => setEditLead(lead)} title="Editar"><Edit2 className="h-4 w-4" /></Button><Button variant="ghost" size="icon" onClick={() => setDeleteConfirm(lead)} title="Excluir" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button></div> },
              ] as DataGridColumn<Lead>[]}
              data={filteredLeads}
              isLoading={isLoading}
              emptyMessage="Nenhum lead encontrado"
              pageSize={20}
            />
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      {showCreate && <LeadFormPanel onClose={() => setShowCreate(false)} onSaved={flashSaved} />}
      {editLead && <LeadFormPanel lead={editLead} onClose={() => setEditLead(null)} onSaved={flashSaved} />}
      {qualifyLead && <QualifyBantModal lead={qualifyLead} onClose={() => setQualifyLead(null)} />}
      {viewLead && <LeadDetailModal lead={viewLead} onClose={() => setViewLead(null)} />}
      {deleteConfirm && <DeleteLeadModal lead={deleteConfirm} onClose={() => setDeleteConfirm(null)} />}

      {/* Bulk update modal */}
      {bulkActionMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) { setBulkActionMode(null); setBulkValue('') } }}>
          <div className="bg-card border rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              <h2 className="text-base font-semibold">
                {bulkActionMode === 'owner' ? 'Alterar responsável' : 'Alterar status'} de {selectedIds.size} lead(s)
              </h2>
              {bulkActionMode === 'owner' ? (
                <div>
                  <label className="text-sm font-medium">Novo responsável</label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">Selecione...</option>
                    {allUsers.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium">Novo status</label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">Selecione...</option>
                    {ACTIVE_STATUSES.map((k) => <option key={k} value={k}>{STATUS_CONFIG[k].label}</option>)}
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setBulkActionMode(null); setBulkValue('') }}>Cancelar</Button>
                <Button onClick={handleBulkApply} disabled={!bulkValue} isLoading={bulkUpdateMutation.isPending}>Aplicar</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showBulkDeleteConfirm}
        title={`Excluir ${selectedIds.size} lead(s)?`}
        message="Os leads selecionados serão removidos. Leads com oportunidades vinculadas serão ignorados. Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={bulkDeleteMutation.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setShowBulkDeleteConfirm(false)}
      />
    </div>
  )
}

function StatCard({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: number | string }) {
  return (
    <Card><CardContent className="p-3">
      <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-muted-foreground" /><span className="text-xs text-muted-foreground">{label}</span></div>
      <div className="text-xl font-bold mt-1">{typeof value === 'number' ? formatNumber(value) : value}</div>
    </CardContent></Card>
  )
}

// ==========================================
// Master-Detail Lead Form Panel
// ==========================================

function LeadFormPanel({ lead, onClose, onSaved }: { lead?: Lead; onClose: () => void; onSaved?: (msg: string) => void }) {
  const isEdit = !!lead
  const currentUser = useAuthStore((s) => s.user)
  // CEO (100) e Gestor Comercial (70) podem alterar responsavel
  const canChangeOwner = (currentUser?.role?.hierarchy_level ?? 0) >= 70

  // Company state
  const [companyMode, setCompanyMode] = useState<'nacional' | 'internacional' | 'existente'>(isEdit ? 'existente' : 'nacional')
  const [companyId, setCompanyId] = useState<string | null>(lead?.company_id || null)
  const [cnpjInput, setCnpjInput] = useState('')
  const [companySearch, setCompanySearch] = useState('')
  const [showAddContact, setShowAddContact] = useState(false)
  const [editingContact, setEditingContact] = useState<Contact | null>(null)

  // International company form
  const [intlForm, setIntlForm] = useState({
    name: '', trading_name: '', tax_id: '', tax_id_type: '', country: '',
    address: '', neighborhood: '', city: '', state: '', postal_code: '',
    phone: '', email: '', website: '', linkedin_url: '', industry: '',
  })

  // Lead form - owner_id defaults to current user
  const [leadForm, setLeadForm] = useState({
    primary_contact_id: lead?.primary_contact_id || '',
    origin: lead?.origin || 'active_prospecting' as LeadOrigin,
    sub_origin: lead?.sub_origin || '',
    owner_id: lead?.owner_id || currentUser?.id || '',
    products_interest: lead?.products_interest || [] as string[],
    notes: lead?.notes || '',
    linkedin_post_url: lead?.linkedin_post_url || '',
    linkedin_profile_url: lead?.linkedin_profile_url || '',
    title: lead?.title || '',
    website: lead?.website || '',
    partner_name: lead?.partner_name || '',
    partner_contact: lead?.partner_contact || '',
    status: lead?.status || '' as LeadStatus | '',
    score: lead?.score ?? 60,
  })

  // Hooks
  const lookupMutation = useLookupCNPJ()
  const createFromCNPJ = useCreateCompanyFromCNPJ()
  const createCompanyMutation = useCreateCompany()
  const createLeadMutation = useCreateLead()
  const updateLeadMutation = useUpdateLead()
  const { data: companyContacts, refetch: refetchContacts } = useCompanyContacts(companyId)
  const { data: companyData } = useCompany(companyId)
  const { data: existingCompanies } = useCompanies(companyMode === 'existente' ? companySearch : undefined)
  const { data: allUsers } = useUsers({ perPage: 100 })
  const [error, setError] = useState('')

  // Dirty-state tracking: flip true on the first form interaction so that
  // accidental backdrop clicks / X / Cancelar prompt before discarding work.
  const dirtyRef = useRef(false)
  const initialMountRef = useRef(true)
  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false
      return
    }
    dirtyRef.current = true
  }, [leadForm, intlForm, cnpjInput, companySearch, companyMode, companyId])

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [duplicateLeads, setDuplicateLeads] = useState<ExistingLeadInfo[] | null>(null)
  const requestClose = () => {
    if (dirtyRef.current) { setShowDiscardConfirm(true); return }
    onClose()
  }

  // CNPJ: Consulta + Cria empresa automaticamente
  const handleCNPJSearch = async () => {
    if (!cnpjInput.trim()) return
    const cleanCNPJ = cnpjInput.replace(/\D/g, '')
    setError('')
    try {
      // Step 1: Check if company already exists
      const result = await lookupMutation.mutateAsync(cleanCNPJ)
      if (result.company_exists && result.existing_company_id) {
        // Regra: ao tentar cadastrar um NOVO lead com CNPJ que já tem leads vinculados,
        // bloquear e exibir aviso com dados do(s) responsável(eis). Em modo edição
        // não há bloqueio — só atualiza a empresa.
        if (!isEdit && result.existing_leads && result.existing_leads.length > 0) {
          setDuplicateLeads(result.existing_leads)
          return
        }
        setCompanyId(result.existing_company_id)
        return
      }
      if (!result.success) {
        setError(result.message)
        return
      }
      // Step 2: Company doesn't exist - create automatically with contacts from QSA
      const company = await createFromCNPJ.mutateAsync(cleanCNPJ)
      setCompanyId(company.id)
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao consultar/criar empresa pelo CNPJ'))
    }
  }

  // Create international company
  const handleCreateIntlCompany = async () => {
    if (!intlForm.name.trim()) { setError('Nome da empresa e obrigatorio'); return }
    setError('')
    try {
      const company = await createCompanyMutation.mutateAsync({
        name: intlForm.name, trading_name: intlForm.trading_name || null,
        tax_id: intlForm.tax_id || null, tax_id_type: intlForm.tax_id_type || null,
        country: intlForm.country || 'Internacional',
        address: intlForm.address || null, neighborhood: intlForm.neighborhood || null,
        city: intlForm.city || null, state: intlForm.state || null, postal_code: intlForm.postal_code || null,
        phone: intlForm.phone || null, email: intlForm.email || null,
        website: intlForm.website || null, linkedin_url: intlForm.linkedin_url || null,
        industry: intlForm.industry || null,
      })
      setCompanyId(company.id)
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao criar empresa'))
    }
  }

  // Save lead
  const handleSaveLead = async () => {
    if (!leadForm.title?.trim()) { setError('Nome do lead é obrigatório'); return }
    if (!companyId) { setError('Selecione ou cadastre uma empresa primeiro'); return }
    setError('')
    try {
      if (isEdit) {
        const updateData: LeadUpdate = {
          primary_contact_id: leadForm.primary_contact_id || null,
          title: leadForm.title.trim(),
          website: leadForm.website || null,
          origin: leadForm.origin,
          sub_origin: leadForm.sub_origin || null,
          owner_id: leadForm.owner_id || null,
          products_interest: leadForm.products_interest,
          notes: leadForm.notes || null,
          linkedin_post_url: leadForm.linkedin_post_url || null,
          linkedin_profile_url: leadForm.linkedin_profile_url || null,
          partner_name: leadForm.partner_name || null,
          partner_contact: leadForm.partner_contact || null,
          score: leadForm.score,
        }
        if (leadForm.status) updateData.status = leadForm.status as LeadStatus
        await updateLeadMutation.mutateAsync({ leadId: lead!.id, data: updateData })
      } else {
        const createData: LeadCreate = {
          company_id: companyId,
          primary_contact_id: leadForm.primary_contact_id || null,
          title: leadForm.title.trim(),
          website: leadForm.website || null,
          origin: leadForm.origin,
          sub_origin: leadForm.sub_origin || null,
          owner_id: leadForm.owner_id || null,
          products_interest: leadForm.products_interest,
          notes: leadForm.notes || null,
          linkedin_post_url: leadForm.linkedin_post_url || null,
          linkedin_profile_url: leadForm.linkedin_profile_url || null,
          partner_name: leadForm.partner_name || null,
          partner_contact: leadForm.partner_contact || null,
        }
        const created = await createLeadMutation.mutateAsync(createData)
        onSaved?.(`Lead "${created?.title || createData.title}" cadastrado com sucesso.`)
      }
      if (isEdit) onSaved?.("Lead atualizado com sucesso.")
      onClose()
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao salvar lead'))
    }
  }

  const toggleProduct = (p: string) => {
    setLeadForm((f) => ({
      ...f,
      products_interest: f.products_interest.includes(p) ? f.products_interest.filter((x) => x !== p) : [...f.products_interest, p],
    }))
  }

  const isPending = createLeadMutation.isPending || updateLeadMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={requestClose}>
      <div className="bg-card w-full max-w-4xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-card border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEdit ? 'Editar Lead' : 'Novo Lead - Cadastro Completo'}</h2>
          <Button variant="ghost" size="icon" onClick={requestClose}><X className="h-5 w-5" /></Button>
        </div>

        <div className="p-6 space-y-6">
          {error && <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>}

          {/* ===== SECTION: EMPRESA ===== */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="h-5 w-5 text-primary" />
              <h3 className="text-base font-semibold">Empresa</h3>
            </div>

            {!isEdit && (
              <div className="flex gap-2 mb-4">
                {(['nacional', 'internacional', 'existente'] as const).map((mode) => (
                  <button key={mode} type="button" onClick={() => { setCompanyMode(mode); setCompanyId(null); setError('') }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${companyMode === mode ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-input hover:bg-muted'}`}>
                    {mode === 'nacional' && <><FileText className="h-4 w-4" />Nacional (CNPJ)</>}
                    {mode === 'internacional' && <><Globe className="h-4 w-4" />Internacional</>}
                    {mode === 'existente' && <><Database className="h-4 w-4" />Existente</>}
                  </button>
                ))}
              </div>
            )}

            {/* Nacional - CNPJ */}
            {companyMode === 'nacional' && !companyId && !isEdit && (
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-sm font-medium">CNPJ</label>
                  <Input placeholder="00.000.000/0000-00" value={cnpjInput} onChange={(e) => setCnpjInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCNPJSearch() } }} />
                </div>
                <div className="flex items-end">
                  <Button onClick={handleCNPJSearch} isLoading={lookupMutation.isPending || createFromCNPJ.isPending} leftIcon={<Search className="h-4 w-4" />}>
                    {lookupMutation.isPending ? 'Consultando...' : createFromCNPJ.isPending ? 'Criando empresa...' : 'Consultar'}
                  </Button>
                </div>
              </div>
            )}

            {/* Internacional - Manual Form */}
            {companyMode === 'internacional' && !companyId && !isEdit && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-sm font-medium">Razao Social *</label><Input value={intlForm.name} onChange={(e) => setIntlForm({ ...intlForm, name: e.target.value })} /></div>
                  <div><label className="text-sm font-medium">Nome Fantasia</label><Input value={intlForm.trading_name} onChange={(e) => setIntlForm({ ...intlForm, trading_name: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-sm font-medium">Pais *</label><Input placeholder="Ex: United States" value={intlForm.country} onChange={(e) => setIntlForm({ ...intlForm, country: e.target.value })} /></div>
                  <div>
                    <label className="text-sm font-medium">Tipo Tax ID</label>
                    <select className={selectClass} value={intlForm.tax_id_type} onChange={(e) => setIntlForm({ ...intlForm, tax_id_type: e.target.value })}>
                      <option value="">Selecione...</option>
                      {Object.entries(TAX_ID_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div><label className="text-sm font-medium">Tax ID</label><Input value={intlForm.tax_id} onChange={(e) => setIntlForm({ ...intlForm, tax_id: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-sm font-medium">Endereco</label><Input value={intlForm.address} onChange={(e) => setIntlForm({ ...intlForm, address: e.target.value })} /></div>
                  <div><label className="text-sm font-medium">Cidade</label><Input value={intlForm.city} onChange={(e) => setIntlForm({ ...intlForm, city: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-sm font-medium">Estado/Provincia</label><Input value={intlForm.state} onChange={(e) => setIntlForm({ ...intlForm, state: e.target.value })} /></div>
                  <div><label className="text-sm font-medium">CEP/Postal</label><Input value={intlForm.postal_code} onChange={(e) => setIntlForm({ ...intlForm, postal_code: e.target.value })} /></div>
                  <div><label className="text-sm font-medium">Telefone</label><Input value={intlForm.phone} onChange={(e) => setIntlForm({ ...intlForm, phone: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-sm font-medium">Email</label><Input type="email" value={intlForm.email} onChange={(e) => setIntlForm({ ...intlForm, email: e.target.value })} /></div>
                  <div><label className="text-sm font-medium">Website</label><Input value={intlForm.website} onChange={(e) => setIntlForm({ ...intlForm, website: e.target.value })} /></div>
                  <div><label className="text-sm font-medium">LinkedIn</label><Input value={intlForm.linkedin_url} onChange={(e) => setIntlForm({ ...intlForm, linkedin_url: e.target.value })} /></div>
                </div>
                <Button onClick={handleCreateIntlCompany} isLoading={createCompanyMutation.isPending} leftIcon={<Plus className="h-4 w-4" />}>
                  Criar Empresa Internacional
                </Button>
              </div>
            )}

            {/* Existente - Search */}
            {companyMode === 'existente' && !companyId && !isEdit && (
              <div className="space-y-3">
                <div><label className="text-sm font-medium">Buscar empresa cadastrada</label>
                  <SearchInput placeholder="Nome, CNPJ ou Tax ID..." value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} onClear={() => setCompanySearch('')} />
                </div>
                {existingCompanies && existingCompanies.length > 0 && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {existingCompanies.map((c) => (
                      <button key={c.id} type="button" onClick={() => setCompanyId(c.id)}
                        className="w-full text-left px-4 py-2 hover:bg-muted/50 border-b last:border-b-0 transition-colors">
                        <div className="font-medium text-sm">{c.trading_name || c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.cnpj || c.tax_id || 'Sem ID fiscal'} | {c.city || ''}{c.state ? `/${c.state}` : ''}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Company Selected - Show Data */}
            {companyId && companyData && (
              <CompanyDataCard company={companyData} onClear={isEdit ? undefined : () => setCompanyId(null)} />
            )}
          </section>

          {/* ===== SECTION: DADOS DO LEAD ===== */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Target className="h-5 w-5 text-primary" />
              <h3 className="text-base font-semibold">Dados do Lead</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Nome do Lead (Nome Fantasia) <span className="text-destructive">*</span></label>
                <Input placeholder="Ex: Projeto IGA Raizen, Compliance Petrobras..." value={leadForm.title} onChange={(e) => setLeadForm({ ...leadForm, title: e.target.value })} required aria-required="true" />
                {!leadForm.title?.trim() && <p className="text-xs text-destructive mt-1">Nome do lead é obrigatório</p>}
              </div>
              <div>
                <label className="text-sm font-medium">Site</label>
                <Input placeholder="https://www.exemplo.com.br" value={leadForm.website} onChange={(e) => setLeadForm({ ...leadForm, website: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Origem *</label>
                  <select className={selectClass} value={leadForm.origin} onChange={(e) => setLeadForm({ ...leadForm, origin: e.target.value as LeadOrigin })}>
                    {Object.entries(ORIGIN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Responsavel</label>
                  {canChangeOwner ? (
                    <select className={selectClass} value={leadForm.owner_id} onChange={(e) => setLeadForm({ ...leadForm, owner_id: e.target.value })}>
                      <option value="">Nenhum</option>
                      {allUsers?.items?.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                    </select>
                  ) : (
                    <div className="flex h-10 items-center px-3 rounded-md border border-input bg-muted/50 text-sm">
                      {currentUser?.full_name || currentUser?.email || '-'}
                    </div>
                  )}
                </div>
              </div>

              {isEdit && (
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <select className={selectClass} value={leadForm.status} onChange={(e) => setLeadForm({ ...leadForm, status: e.target.value as LeadStatus })}>
                    <option value="">Manter atual</option>
                    {ACTIVE_STATUSES.map((k) => <option key={k} value={k}>{STATUS_CONFIG[k].label}</option>)}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">Temperatura é derivada do preenchimento do BANT (na aba do lead).</p>
                </div>
              )}

              {leadForm.origin === 'event' && (
                <div>
                  <label className="text-sm font-medium">Nome do Evento *</label>
                  <SubOriginCombobox
                    origin="event"
                    placeholder="Ex: IAM Tech Day 2026, RSA Conference, Gartner Summit..."
                    value={leadForm.sub_origin}
                    onChange={(v) => setLeadForm({ ...leadForm, sub_origin: v })}
                  />
                </div>
              )}

              {leadForm.origin === 'bdr_partners' && (
                <div>
                  <label className="text-sm font-medium">Nome do BDR/Parceiro/Indicação *</label>
                  <SubOriginCombobox
                    origin="bdr_partners"
                    placeholder="Ex: Maria Silva, Empresa Parceira X, João (indicação)"
                    value={leadForm.sub_origin}
                    onChange={(v) => setLeadForm({ ...leadForm, sub_origin: v })}
                  />
                </div>
              )}

              {leadForm.origin === 'active_prospecting' && (
                <div>
                  <label className="text-sm font-medium">Forma de Prospecção *</label>
                  <SubOriginCombobox
                    origin="active_prospecting"
                    placeholder="Ex: LinkedIn, base própria de leads, WhatsApp, e-mail frio..."
                    value={leadForm.sub_origin}
                    onChange={(v) => setLeadForm({ ...leadForm, sub_origin: v })}
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Produtos de Interesse</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {PRODUCT_OPTIONS.map((p) => (
                    <button key={p} type="button" onClick={() => toggleProduct(p)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${leadForm.products_interest.includes(p) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-input hover:bg-muted'}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Observacoes</label>
                <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px]" value={leadForm.notes} onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} />
              </div>
            </div>
          </section>

          {/* ===== SECTION: CONTATOS (Detail) ===== */}
          {companyId && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  <h3 className="text-base font-semibold">Contatos da Empresa</h3>
                  {companyContacts && <Badge variant="secondary">{companyContacts.length}</Badge>}
                </div>
                <Button size="sm" variant="outline" onClick={() => { setEditingContact(null); setShowAddContact(true) }} leftIcon={<UserPlus className="h-4 w-4" />}>
                  Adicionar Contato
                </Button>
              </div>

              {/* Primary contact selector */}
              {companyContacts && companyContacts.length > 0 && (
                <div className="mb-3">
                  <label className="text-sm font-medium">Contato Principal do Lead</label>
                  <select className={selectClass} value={leadForm.primary_contact_id} onChange={(e) => setLeadForm({ ...leadForm, primary_contact_id: e.target.value })}>
                    <option value="">Nenhum selecionado</option>
                    {companyContacts.map((c) => <option key={c.id} value={c.id}>{c.display_name || c.first_name}{c.job_title ? ` - ${c.job_title}` : ''}{c.email ? ` (${c.email})` : ''}</option>)}
                  </select>
                </div>
              )}

              {/* Contacts Grid */}
              <ContactsGrid
                contacts={companyContacts || []}
                companyId={companyId}
                onEdit={(c) => { setEditingContact(c); setShowAddContact(true) }}
                onRefetch={refetchContacts}
              />

              {/* Add/Edit Contact Modal */}
              {showAddContact && (
                <ContactFormModal
                  contact={editingContact}
                  companyId={companyId}
                  onClose={() => { setShowAddContact(false); setEditingContact(null) }}
                  onSaved={() => { refetchContacts(); setShowAddContact(false); setEditingContact(null) }}
                />
              )}
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t px-6 py-4 flex justify-end gap-3">
          <Button variant="outline" onClick={requestClose}>Cancelar</Button>
          <Button onClick={handleSaveLead} isLoading={isPending} disabled={!companyId}>
            {isEdit ? 'Salvar Alteracoes' : 'Criar Lead'}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={showDiscardConfirm}
        title="Descartar alterações?"
        message="Os dados preenchidos no formulário não serão salvos. Tem certeza que deseja sair?"
        confirmLabel="Descartar"
        cancelLabel="Continuar editando"
        variant="warning"
        onConfirm={() => { setShowDiscardConfirm(false); onClose() }}
        onCancel={() => setShowDiscardConfirm(false)}
      />

      {duplicateLeads && (
        <DuplicateLeadDialog leads={duplicateLeads} onClose={() => { setDuplicateLeads(null); onClose() }} />
      )}
    </div>
  )
}

// ==========================================
// Duplicate Lead Dialog — bloqueia cadastro de lead com CNPJ já existente
// ==========================================
function DuplicateLeadDialog({ leads, onClose }: { leads: ExistingLeadInfo[]; onClose: () => void }) {
  const cleanWhats = (p?: string | null) => (p || '').replace(/\D/g, '')
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/15 text-amber-500 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Cadastro de Lead já existente</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Se possui uma oportunidade quente vinculada a este cadastro, por gentileza, procurar o Responsável pelo lead já existente.
              </p>
            </div>
          </div>

          <div className="space-y-3 mt-4 max-h-[50vh] overflow-y-auto">
            {leads.map((l) => (
              <div key={l.lead_id} className="border rounded-lg p-3 bg-muted/30">
                <div className="text-sm font-medium truncate">{l.title || '(sem título)'}</div>
                {l.status && <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{l.status}</div>}
                {l.owner ? (
                  <div className="mt-2 space-y-1 text-xs">
                    <div className="text-muted-foreground">Responsável</div>
                    <div className="font-medium">{l.owner.full_name || '—'}</div>
                    {l.owner.email && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Email:</span>
                        <a href={`mailto:${l.owner.email}`} className="text-primary hover:underline truncate">{l.owner.email}</a>
                      </div>
                    )}
                    {l.owner.phone && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">WhatsApp:</span>
                        <a href={`https://wa.me/${cleanWhats(l.owner.phone)}`} target="_blank" rel="noreferrer" className="text-green-600 dark:text-green-400 hover:underline">{l.owner.phone}</a>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground mt-2">Lead sem responsável atribuído.</div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-5">
            <Button onClick={onClose}>Entendi</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ==========================================
// Company Data Card (readonly display)
// ==========================================

function CompanyDataCard({ company, onClear }: { company: Company; onClear?: () => void }) {
  const [showDetails, setShowDetails] = useState(false)

  return (
    <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-semibold text-base">{company.trading_name || company.name}</div>
          {company.trading_name && <div className="text-sm text-muted-foreground">{company.name}</div>}
        </div>
        <div className="flex items-center gap-2">
          {company.cnpj && <Badge variant="outline">CNPJ: {company.cnpj}</Badge>}
          {company.tax_id && <Badge variant="outline">{company.tax_id_type}: {company.tax_id}</Badge>}
          {company.data_source === 'cnpj_api' && <Badge variant="info">BrasilAPI</Badge>}
          {onClear && <Button variant="ghost" size="icon" onClick={onClear} title="Trocar empresa"><X className="h-4 w-4" /></Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-sm">
        {company.address && <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-muted-foreground" />{company.address}{company.neighborhood ? `, ${company.neighborhood}` : ''}</div>}
        {company.city && <div>{company.city}{company.state ? `/${company.state}` : ''}{company.postal_code ? ` - ${company.postal_code}` : ''}</div>}
        {company.phone && <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{company.phone}</div>}
        {company.email && <div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{company.email}</div>}
        {company.linkedin_url && <div className="flex items-center gap-1.5"><Linkedin className="h-3.5 w-3.5 text-muted-foreground" /><a href={company.linkedin_url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate">LinkedIn</a></div>}
        {company.industry && <div className="text-muted-foreground">Setor: {company.industry}</div>}
      </div>

      {/* Expandable Receita Federal data */}
      {company.is_brazilian && company.situacao_cadastral && (
        <>
          <button type="button" onClick={() => setShowDetails(!showDetails)} className="text-xs text-primary hover:underline">
            {showDetails ? 'Ocultar' : 'Ver'} dados da Receita Federal
          </button>
          {showDetails && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-xs text-muted-foreground border-t pt-2">
              <div>Situacao: <strong className="text-foreground">{company.situacao_cadastral}</strong></div>
              {company.natureza_juridica && <div>Natureza: {company.natureza_juridica}</div>}
              {company.porte && <div>Porte: {company.porte}</div>}
              {company.capital_social != null && <div>Capital: R$ {company.capital_social.toLocaleString('pt-BR')}</div>}
              {company.cnae_principal && <div className="col-span-2">CNAE: {company.cnae_principal} - {company.cnae_principal_descricao}</div>}
              {company.data_abertura && <div>Abertura: {company.data_abertura}</div>}
              <div>Simples: {company.simples_nacional ? 'Sim' : 'Nao'} | MEI: {company.mei ? 'Sim' : 'Nao'}</div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ==========================================
// Contacts Grid
// ==========================================

function ContactsGrid({ contacts, companyId, onEdit, onRefetch }: { contacts: Contact[]; companyId: string; onEdit: (c: Contact) => void; onRefetch: () => void }) {
  const deleteMutation = useDeleteContact()
  const setPrimaryMutation = useSetPrimaryContact()
  const [confirmDeactivate, setConfirmDeactivate] = useState<Contact | null>(null)

  const handleSetPrimary = async (contactId: string) => {
    try {
      await setPrimaryMutation.mutateAsync(contactId)
      onRefetch()
    } catch { /* ignore */ }
  }

  const handleDelete = async (contactId: string) => {
    try {
      await deleteMutation.mutateAsync(contactId)
      onRefetch()
    } catch { /* ignore */ }
  }

  if (contacts.length === 0) {
    return <div className="text-center py-6 text-muted-foreground text-sm border rounded-lg">Nenhum contato cadastrado. Adicione contatos manualmente.</div>
  }

  return (
    <div className="border rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left p-2 font-medium w-8">
              <Star className="h-3.5 w-3.5 text-muted-foreground" />
            </th>
            <th className="text-left p-2 font-medium">Nome</th>
            <th className="text-left p-2 font-medium">Cargo</th>
            <th className="text-left p-2 font-medium">Email</th>
            <th className="text-left p-2 font-medium">Telefone</th>
            <th className="text-left p-2 font-medium">WhatsApp</th>
            <th className="text-left p-2 font-medium">LinkedIn</th>
            <th className="text-left p-2 font-medium">Origem</th>
            <th className="text-right p-2 font-medium">Acoes</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((c) => (
            <tr key={c.id} className="border-b last:border-b-0 hover:bg-muted/20 transition-colors">
              <td className="p-2">
                <button type="button" onClick={() => handleSetPrimary(c.id)} title={c.is_primary ? 'Contato principal' : 'Definir como principal'}>
                  <Star className={`h-4 w-4 ${c.is_primary ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground hover:text-amber-400'}`} />
                </button>
              </td>
              <td className="p-2">
                <div className="font-medium">{c.display_name || c.first_name}</div>
                {c.source === 'qsa_cnpj' && <Badge variant="outline" className="text-[9px] px-1 py-0 mt-0.5">QSA</Badge>}
              </td>
              <td className="p-2 text-muted-foreground">{c.job_title || c.qsa_qualification || '-'}</td>
              <td className="p-2">
                {c.email ? (
                  <div className="flex items-center gap-1">
                    <span className="truncate" title={c.email}>{c.email}</span>
                    <CopyButton value={c.email} label="email" size="xs" />
                  </div>
                ) : <span className="text-muted-foreground text-xs">-</span>}
              </td>
              <td className="p-2">
                {(c.phone || c.mobile) ? (
                  <div className="flex items-center gap-1">
                    <span title={c.phone || c.mobile}>{c.phone || c.mobile}</span>
                    <CopyButton value={c.phone || c.mobile} label="telefone" size="xs" />
                  </div>
                ) : <span className="text-muted-foreground text-xs">-</span>}
              </td>
              <td className="p-2">
                {c.whatsapp ? (
                  <div className="flex items-center gap-1">
                    <span title={c.whatsapp}>{c.whatsapp}</span>
                    <CopyButton value={c.whatsapp} label="whatsapp" size="xs" />
                  </div>
                ) : <span className="text-muted-foreground text-xs">-</span>}
              </td>
              <td className="p-2">{c.linkedin_url ? <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">Ver</a> : <span className="text-muted-foreground text-xs">-</span>}</td>
              <td className="p-2"><Badge variant="outline" className="text-[10px] px-1.5 py-0">{c.source === 'qsa_cnpj' ? 'Receita' : c.source || 'Manual'}</Badge></td>
              <td className="p-2">
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" onClick={() => onEdit(c)} title="Editar"><Edit2 className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setConfirmDeactivate(c)} title="Desativar" className="text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ConfirmDialog
        open={!!confirmDeactivate}
        title="Desativar contato?"
        message={`O contato "${confirmDeactivate?.display_name || confirmDeactivate?.first_name || ''}" será desativado.`}
        confirmLabel="Desativar"
        variant="warning"
        isLoading={deleteMutation.isPending}
        onConfirm={async () => {
          if (!confirmDeactivate) return
          await handleDelete(confirmDeactivate.id)
          setConfirmDeactivate(null)
        }}
        onCancel={() => setConfirmDeactivate(null)}
      />
    </div>
  )
}

// (ContactFormModal extraído para components/ContactFormModal.tsx)
function _ContactFormModal_DEPRECATED({ contact, companyId, onClose, onSaved }: { contact: Contact | null; companyId: string; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!contact
  const createMutation = useCreateContact()
  const updateMutation = useUpdateContact()
  const [form, setForm] = useState({
    first_name: contact?.first_name || '',
    last_name: contact?.last_name || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    mobile: contact?.mobile || '',
    whatsapp: contact?.whatsapp || '',
    linkedin_url: contact?.linkedin_url || '',
    instagram: contact?.instagram || '',
    twitter: contact?.twitter || '',
    job_title: contact?.job_title || '',
    department: contact?.department || '',
    role: (contact?.role || '') as ContactRole | '',
    notes: contact?.notes || '',
  })
  const [error, setError] = useState('')

  const dirtyRef = useRef(false)
  const initialMountRef = useRef(true)
  useEffect(() => {
    if (initialMountRef.current) { initialMountRef.current = false; return }
    dirtyRef.current = true
  }, [form])

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const requestClose = () => {
    if (dirtyRef.current) { setShowDiscardConfirm(true); return }
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.first_name.trim()) { setError('Nome e obrigatorio'); return }
    setError('')
    try {
      if (isEdit) {
        const updateData: ContactUpdate = {
          first_name: form.first_name, last_name: form.last_name || null,
          email: form.email || null, phone: form.phone || null, mobile: form.mobile || null,
          whatsapp: form.whatsapp || null, linkedin_url: form.linkedin_url || null,
          instagram: form.instagram || null, twitter: form.twitter || null,
          job_title: form.job_title || null, department: form.department || null,
          role: (form.role as ContactRole) || null, notes: form.notes || null,
        }
        await updateMutation.mutateAsync({ contactId: contact!.id, data: updateData })
      } else {
        const createData: ContactCreate = {
          company_id: companyId, first_name: form.first_name, last_name: form.last_name || null,
          email: form.email || null, phone: form.phone || null, mobile: form.mobile || null,
          whatsapp: form.whatsapp || null, linkedin_url: form.linkedin_url || null,
          instagram: form.instagram || null, twitter: form.twitter || null,
          job_title: form.job_title || null, department: form.department || null,
          role: (form.role as ContactRole) || null, notes: form.notes || null,
        }
        await createMutation.mutateAsync(createData)
      }
      onSaved()
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao salvar contato'))
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={requestClose}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{isEdit ? 'Editar Contato' : 'Novo Contato'}</h2>
          <Button variant="ghost" size="icon" onClick={requestClose}><X className="h-4 w-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {error && <div className="p-2 rounded bg-destructive/10 text-destructive text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium">Nome *</label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></div>
            <div><label className="text-sm font-medium">Sobrenome</label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium">Cargo</label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Departamento</label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
          </div>
          <div>
            <label className="text-sm font-medium">Papel na Empresa</label>
            <select className={selectClass} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as ContactRole | '' })}>
              <option value="">Nenhum</option>
              {Object.entries(CONTACT_ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium">Email</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Telefone</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium">Celular</label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            <div><label className="text-sm font-medium">WhatsApp</label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-sm font-medium">LinkedIn</label><Input placeholder="URL completa" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Instagram</label><Input placeholder="username" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Twitter/X</label><Input placeholder="username" value={form.twitter} onChange={(e) => setForm({ ...form, twitter: e.target.value })} /></div>
          </div>
          <div>
            <label className="text-sm font-medium">Observacoes</label>
            <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[50px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={requestClose}>Cancelar</Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>{isEdit ? 'Salvar' : 'Adicionar Contato'}</Button>
          </div>
        </form>
      </div>

      <ConfirmDialog
        open={showDiscardConfirm}
        title="Descartar alterações?"
        message="Os dados preenchidos no formulário do contato não serão salvos. Tem certeza que deseja sair?"
        confirmLabel="Descartar"
        cancelLabel="Continuar editando"
        variant="warning"
        onConfirm={() => { setShowDiscardConfirm(false); onClose() }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </div>
  )
}

// ==========================================
// Lead Timeline — aggregates events from lead/opps/tasks
// ==========================================

const CHANNEL_ICONS_TIMELINE: Record<ActivityChannel, React.ReactNode> = {
  email: <Mail className="h-3.5 w-3.5" />,
  whatsapp: <MessageCircle className="h-3.5 w-3.5" />,
  phone: <Phone className="h-3.5 w-3.5" />,
  meeting: <Users className="h-3.5 w-3.5" />,
  note: <StickyNote className="h-3.5 w-3.5" />,
}

function formatTimeOnly(s: string): string {
  return new Date(s).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function LeadTimeline({ lead }: { lead: Lead }) {
  const { data, isLoading } = useLeadTimeline(lead.id)
  const events = data?.items || []
  const currentUser = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const updateActivityMut = useUpdateActivity()
  const deleteActivityMut = useDeleteActivity()
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<{ activityId: string; oppId: string } | null>(null)

  const iconFor = (type: string): { Icon: any; color: string } => {
    if (type.startsWith('lead_created')) return { Icon: UserPlus, color: 'text-blue-500' }
    if (type === 'lead_qualified') return { Icon: ClipboardCheck, color: 'text-emerald-500' }
    if (type === 'lead_converted') return { Icon: Award, color: 'text-amber-500' }
    if (type === 'task_created') return { Icon: ClipboardList, color: 'text-slate-500' }
    if (type === 'task_completed') return { Icon: CheckCircle, color: 'text-green-500' }
    if (type.startsWith('outreach_email')) return { Icon: Mail, color: 'text-amber-500' }
    if (type.startsWith('outreach_whatsapp')) return { Icon: Phone, color: 'text-green-500' }
    if (type.startsWith('outreach_')) return { Icon: TrendingUp, color: 'text-blue-400' }
    if (type === 'opportunity_created') return { Icon: Target, color: 'text-purple-500' }
    if (type === 'opportunity_won') return { Icon: CheckCircle, color: 'text-green-500' }
    if (type === 'opportunity_lost') return { Icon: X, color: 'text-destructive' }
    if (type.startsWith('opportunity_activity_')) return { Icon: FileText, color: 'text-indigo-500' }
    return { Icon: Clock, color: 'text-muted-foreground' }
  }

  const grouped = useMemo(() => {
    const groups: Record<string, typeof events> = {}
    const today = new Date()
    const yest = new Date(today); yest.setDate(yest.getDate() - 1)
    for (const e of events) {
      if (!e.timestamp) continue
      const d = new Date(e.timestamp)
      let label: string
      if (d.toDateString() === today.toDateString()) label = 'Hoje'
      else if (d.toDateString() === yest.toDateString()) label = 'Ontem'
      else label = formatDate(e.timestamp)
      if (!groups[label]) groups[label] = []
      groups[label].push(e)
    }
    return Object.entries(groups).map(([label, items]) => ({ label, items }))
  }, [events])

  const refresh = () => qc.invalidateQueries({ queryKey: ['lead-timeline', lead.id] })

  if (isLoading) return <p className="text-sm text-muted-foreground text-center py-6">Carregando histórico...</p>
  if (events.length === 0) return <p className="text-sm text-muted-foreground text-center py-6">Nenhum evento registrado.</p>

  return (
    <div className="space-y-6">
      {grouped.map((group) => (
        <div key={group.label}>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium text-muted-foreground px-2">{group.label}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="space-y-3">
            {group.items.map((e, i) => {
              const isActivity = e.type.startsWith('opportunity_activity_')
              const channelKey = (e.extra?.channel as ActivityChannel | undefined) || 'note'
              const chConfig = isActivity ? CHANNEL_CONFIG[channelKey] : null
              const activityId = isActivity ? (e.extra?.activity_id as string | undefined) : undefined
              const oppId = isActivity ? (e.extra?.opportunity_id as string | undefined) : undefined
              const isOwn = isActivity && !!currentUser?.id && currentUser.id === e.extra?.user_id
              const isEditingThis = isActivity && editingActivityId === activityId
              const wasEdited = !!e.updated_at && e.updated_at !== e.timestamp

              // Activity event: card with avatar, channel badge, edit/delete on hover
              if (isActivity && activityId && oppId) {
                return (
                  <div key={activityId} className="flex gap-3">
                    <UserAvatar name={e.user_name || 'N/A'} size="sm" className="mt-1 shrink-0" />
                    <div className="flex-1 rounded-lg border bg-card p-3 shadow-sm group">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{e.user_name || 'Usuario'}</span>
                          {chConfig && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${chConfig.bg} text-white`}>
                              {CHANNEL_ICONS_TIMELINE[channelKey]}
                              {chConfig.label}
                            </span>
                          )}
                          {wasEdited && (
                            <span className="text-[10px] text-muted-foreground italic" title={`Editado em ${formatTimeOnly(e.updated_at!)}`}>(editado)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">{formatTimeOnly(e.timestamp)}</span>
                          {isOwn && !isEditingThis && (
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => { setEditingActivityId(activityId); setEditingContent(e.content || '') }}
                                className="p-1 rounded hover:bg-muted"
                                title="Editar"
                              >
                                <Pencil className="h-3 w-3 text-muted-foreground" />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm({ activityId, oppId })}
                                className="p-1 rounded hover:bg-muted"
                                title="Excluir"
                              >
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {isEditingThis ? (
                        <div className="space-y-2">
                          <textarea
                            value={editingContent}
                            onChange={(ev) => setEditingContent(ev.target.value)}
                            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px]"
                            placeholder="Editar interação..."
                            autoFocus
                          />
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditingActivityId(null); setEditingContent('') }}
                              disabled={updateActivityMut.isPending}
                            >
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              onClick={async () => {
                                const trimmed = editingContent.trim()
                                if (!trimmed) return
                                await updateActivityMut.mutateAsync({ oppId, activityId, content: trimmed })
                                await refresh()
                                setEditingActivityId(null)
                                setEditingContent('')
                              }}
                              disabled={updateActivityMut.isPending || !editingContent.trim()}
                            >
                              {updateActivityMut.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                              Salvar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{e.content}</p>
                      )}
                    </div>
                  </div>
                )
              }

              // Non-activity event: simple row with icon dot
              const { Icon, color } = iconFor(e.type)
              return (
                <div key={`${e.type}-${e.timestamp}-${i}`} className="relative pl-7">
                  <div className={`absolute left-0 top-0.5 w-5 h-5 rounded-full bg-card border-2 border-current ${color} flex items-center justify-center`}>
                    <Icon className="w-2.5 h-2.5" />
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-sm font-medium">{e.title}</p>
                    <span className="text-xs text-muted-foreground flex-shrink-0">{formatTimeOnly(e.timestamp)}</span>
                  </div>
                  {e.content && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{e.content}</p>}
                  {e.user_name && <p className="text-[10px] text-muted-foreground/70 mt-0.5">por {e.user_name}</p>}
                </div>
              )
            })}
          </div>
        </div>
      ))}

      <ConfirmDialog
        open={!!deleteConfirm}
        title="Excluir interação?"
        message="Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="destructive"
        isLoading={deleteActivityMut.isPending}
        onConfirm={async () => {
          if (!deleteConfirm) return
          try {
            await deleteActivityMut.mutateAsync(deleteConfirm)
            await refresh()
          } catch { /* erro tratado pelo react-query */ }
          setDeleteConfirm(null)
        }}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  )
}


// (QualifyModal extraído para components/QualifyBantModal.tsx — único formulário canônico de BANT)

// ==========================================
// Lead Detail Modal (App-style)
// ==========================================

function LeadDetailModal({ lead: initialLead, onClose }: { lead: Lead; onClose: () => void }) {
  // Sempre lê o lead "fresco" do cache do React Query — a prop é só seed inicial.
  // Sem isso, edições inline (sub_origin, title, etc) só apareciam após refresh,
  // pois o objeto passado por viewLead em LeadsPage não é re-sincronizado com a lista.
  const { data: freshLead } = useLead(initialLead.id)
  const lead = freshLead || initialLead
  const [tab, setTab] = useState<'info' | 'contacts' | 'bant' | 'opportunities' | 'tasks' | 'history'>('info')
  const [editingNotes, setEditingNotes] = useState(false)
  const [notesDraft, setNotesDraft] = useState('')
  const [editContact, setEditContact] = useState<Contact | null>(null)
  const [showContactForm, setShowContactForm] = useState(false)
  const [showOppForm, setShowOppForm] = useState(false)
  const [deleteContactConfirm, setDeleteContactConfirm] = useState<Contact | null>(null)
  const deleteContactMut = useDeleteContact()
  const { data: contactsData, refetch: refetchContacts } = useCompanyContacts(lead.company_id)
  const contacts = contactsData || []
  const updateLead = useUpdateLead()
  const createOpp = useCreateOpportunity()
  const navigate = useNavigate()
  const [oppForm, setOppForm] = useState({ name: "", value: 0, probability: 50, expected_close_date: "" })

  // Owner inline edit — só admin (hierarchy >= 70) pode reatribuir; demais veem read-only.
  const currentUser = useAuthStore((s) => s.user)
  const canChangeOwner = (currentUser?.role?.hierarchy_level ?? 0) >= 70
  const { data: usersData } = useUsers({ perPage: 100 })
  const ownerOptions = useMemo(
    () => (usersData?.items || [])
      .filter((u) => u.is_active !== false)
      .map((u) => ({ value: u.id, label: u.full_name || u.email || u.id })),
    [usersData?.items]
  )

  // Próxima ação: task pendente do lead com menor due_at
  const { data: tasksData } = useTasks({ lead_id: lead.id })
  const nextAction = (() => {
    const pending = (tasksData?.items || []).filter(t => t.status === 'pending' && t.due_at)
    if (!pending.length) return null
    pending.sort((a, b) => parseDate(a.due_at!).getTime() - parseDate(b.due_at!).getTime())
    const t = pending[0]
    const dueMs = parseDate(t.due_at!).getTime()
    const overdue = dueMs < Date.now()
    const diffDays = Math.round((dueMs - Date.now()) / (24 * 60 * 60 * 1000))
    return { task: t, overdue, diffDays }
  })()

  // Score breakdown — só carrega se a aba info estiver ativa
  const { data: scoreBreakdown } = useLeadScoreBreakdown(tab === 'info' ? lead.id : null)

  // Oportunidades vinculadas ao lead — só carrega se a aba estiver ativa
  const { data: leadOppsData, isLoading: isLoadingLeadOpps } = useOpportunities(
    tab === 'opportunities' ? { leadId: lead.id, perPage: 100 } : {}
  )
  const linkedOpenOpps = useMemo(() => {
    if (tab !== 'opportunities') return []
    const items = leadOppsData?.items || []
    return items.filter(o => o.stage !== 'won' && o.stage !== 'lost')
  }, [leadOppsData, tab])

  // Inline edit do CNPJ da empresa
  const updateCompanyMut = useUpdateCompany()
  const syncCnpjMut = useSyncCompanyCnpj()
  const [editingCnpj, setEditingCnpj] = useState(false)
  const [cnpjDraft, setCnpjDraft] = useState('')
  // Modal canônico de qualificação BANT — substitui os toggles antigos
  const [qualifyLeadModal, setQualifyLeadModal] = useState<Lead | null>(null)
  const [cnpjError, setCnpjError] = useState('')

  const startCnpjEdit = () => {
    setCnpjDraft(lead.company?.cnpj || '')
    setCnpjError('')
    setEditingCnpj(true)
  }
  const saveCnpj = async () => {
    if (!lead.company_id) return
    const cleaned = cnpjDraft.replace(/\D/g, '')
    if (cleaned && cleaned.length !== 14) {
      setCnpjError('CNPJ deve ter 14 dígitos')
      return
    }
    try {
      await updateCompanyMut.mutateAsync({
        companyId: lead.company_id,
        data: { cnpj: cleaned || null } as any,
      })
      // Se há CNPJ válido, SEMPRE sincroniza com a Receita (mesmo se for o mesmo CNPJ —
      // user pode estar querendo re-puxar). Sem CNPJ não tem o que sincronizar.
      if (cleaned) {
        try {
          await syncCnpjMut.mutateAsync({ companyId: lead.company_id, syncContacts: false })
        } catch (syncErr: any) {
          setCnpjError('CNPJ salvo, mas sincronização com Receita falhou: ' + (syncErr?.response?.data?.detail || 'tente novamente'))
          return
        }
      }
      setEditingCnpj(false)
      setCnpjError('')
    } catch (e: any) {
      setCnpjError(e?.response?.data?.detail || 'Erro ao atualizar CNPJ')
    }
  }

  const statusCfg = STATUS_CONFIG[lead.status] || { label: lead.status, variant: 'secondary' as const }
  const scoreCfg = getTemperature(lead)

  const [bantState, setBantState] = useState({
    budget_confirmed: lead.budget_confirmed,
    authority_confirmed: lead.authority_confirmed,
    need_confirmed: lead.need_confirmed,
    timeline_confirmed: lead.timeline_confirmed,
  })
  useEffect(() => {
    setBantState({
      budget_confirmed: lead.budget_confirmed,
      authority_confirmed: lead.authority_confirmed,
      need_confirmed: lead.need_confirmed,
      timeline_confirmed: lead.timeline_confirmed,
    })
  }, [lead.id, lead.budget_confirmed, lead.authority_confirmed, lead.need_confirmed, lead.timeline_confirmed])
  const bantScoreLocal = (Object.values(bantState) as boolean[]).filter(Boolean).length

  const handleBANTToggle = (field: keyof typeof bantState, value: boolean) => {
    setBantState(prev => ({ ...prev, [field]: value }))
    updateLead.mutate(
      { leadId: lead.id, data: { [field]: value } },
      { onError: () => setBantState(prev => ({ ...prev, [field]: !value })) },
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border rounded-xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-card">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold truncate">{lead.company?.name || 'Sem empresa'}</h2>
              <div className="flex items-center gap-3 mt-0.5">
                <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                <span className={`text-sm font-semibold ${scoreCfg.color}`}>{scoreCfg.label} ({lead.score})</span>
                {lead.origin && <span className="text-xs text-muted-foreground">{ORIGIN_LABELS[lead.origin] || lead.origin}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="default" size="sm" onClick={() => setShowOppForm(true)}>
              <TrendingUp className="h-4 w-4 mr-1" /> Criar Oportunidade
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="h-5 w-5" /></Button>
          </div>
        </div>

        {/* ── Próxima Ação Banner ── */}
        {nextAction && (
          <div className={`px-6 py-2.5 border-b flex items-center gap-3 text-sm ${nextAction.overdue ? 'bg-destructive/10 text-destructive' : 'bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200'}`}>
            <Clock className={`h-4 w-4 flex-shrink-0 ${nextAction.overdue ? 'text-destructive' : 'text-amber-600'}`} />
            <span className="font-semibold">Próxima ação:</span>
            <span className="truncate">{nextAction.task.title}</span>
            <span className="ml-auto text-xs font-medium flex-shrink-0">
              {nextAction.overdue
                ? `Atrasada há ${Math.abs(nextAction.diffDays)}d · venceu ${formatDateTime(nextAction.task.due_at!)}`
                : nextAction.diffDays === 0
                  ? `Vence hoje (${formatDateTime(nextAction.task.due_at!)})`
                  : `Vence em ${nextAction.diffDays}d (${formatDateTime(nextAction.task.due_at!)})`}
            </span>
            <button onClick={() => setTab('tasks')} className="text-xs underline hover:no-underline flex-shrink-0">Ver tarefas</button>
          </div>
        )}

        {/* ── Tab Navigation ── */}
        <div className="flex border-b px-6 bg-muted/30">
          {([
            { id: 'info' as const, label: 'Informacoes', icon: FileText },
            { id: 'contacts' as const, label: `Contatos (${contacts.length})`, icon: Users },
            { id: 'bant' as const, label: `BANT (${bantScoreLocal}/4)`, icon: ClipboardCheck },
            { id: 'opportunities' as const, label: 'Oportunidades Vinculadas', icon: TrendingUp },
            { id: 'tasks' as const, label: 'Tarefas', icon: ClipboardList },
            { id: 'history' as const, label: 'Histórico', icon: Clock },
          ]).map(t => (
            <button
              key={t.id}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              onClick={() => setTab(t.id)}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* === TAB: Info === */}
          {tab === 'info' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Left Column */}
              <div className="space-y-5">

                {/* Company Card */}
                {lead.company && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Empresa</h3>
                    <div className="space-y-2">
                      <InfoRow icon={Building2} label="Razao Social" value={lead.company.legal_name || lead.company.name} />

                      {/* CNPJ — editável inline */}
                      <div className="flex items-start gap-3 text-sm">
                        <Database className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-muted-foreground text-xs block">CNPJ</span>
                          {editingCnpj ? (
                            <div className="mt-1 space-y-1">
                              <div className="flex items-center gap-1">
                                <Input
                                  autoFocus
                                  value={cnpjDraft}
                                  onChange={(e) => setCnpjDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); saveCnpj() }
                                    if (e.key === 'Escape') { e.preventDefault(); setEditingCnpj(false); setCnpjError('') }
                                  }}
                                  placeholder="00.000.000/0000-00"
                                  className="h-8 text-sm font-mono"
                                  disabled={updateCompanyMut.isPending || syncCnpjMut.isPending}
                                />
                                <Button size="sm" onClick={saveCnpj} disabled={updateCompanyMut.isPending || syncCnpjMut.isPending}>
                                  {syncCnpjMut.isPending ? 'Sincronizando...' : updateCompanyMut.isPending ? 'Salvando...' : 'Salvar'}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => { setEditingCnpj(false); setCnpjError('') }} disabled={updateCompanyMut.isPending || syncCnpjMut.isPending}>
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                              {syncCnpjMut.isPending && <p className="text-xs text-muted-foreground">Buscando dados na Receita Federal...</p>}
                              {cnpjError && <p className="text-xs text-destructive">{cnpjError}</p>}
                            </div>
                          ) : (
                            <button
                              onClick={startCnpjEdit}
                              className="group flex items-center gap-2 hover:text-primary transition-colors text-left"
                              title="Clique para editar"
                            >
                              <span className="font-mono">{lead.company.cnpj || '— Não informado —'}</span>
                              <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          )}
                        </div>
                      </div>

                      {lead.company.industry && <InfoRow icon={Globe} label="Setor" value={lead.company.industry} />}
                      {lead.company.segment && <InfoRow icon={Target} label="Segmento" value={lead.company.segment} />}

                      {/* Website — sempre visível, fallback de lead.website, salva em company.website */}
                      <EditableField
                        icon={Globe}
                        label="Website"
                        value={lead.company.website || lead.website || ''}
                        type="text"
                        placeholder="https://www.exemplo.com.br"
                        alwaysShow
                        onSave={async (v) => {
                          if (!lead.company_id) throw new Error('Empresa não associada')
                          await updateCompanyMut.mutateAsync({ companyId: lead.company_id, data: { website: v } as any })
                        }}
                      />

                      {(lead.company.city || lead.company.state) && (
                        <InfoRow icon={MapPin} label="Localizacao" value={[lead.company.city, lead.company.state].filter(Boolean).join(' - ')} />
                      )}
                    </div>
                  </div>
                )}

                {/* Primary Contact Quick View */}
                {lead.primary_contact && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contato Principal</h3>
                      <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        {lead.primary_contact.first_name?.[0]}{lead.primary_contact.last_name?.[0] || ''}
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{lead.primary_contact.display_name}</p>
                        {lead.primary_contact.job_title && <p className="text-xs text-muted-foreground">{lead.primary_contact.job_title}</p>}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      {lead.primary_contact.email && (
                        <div className="flex items-center gap-1 text-sm">
                          <a href={`mailto:${lead.primary_contact.email}`} className="flex items-center gap-2 flex-1 text-muted-foreground hover:text-primary transition-colors min-w-0">
                            <Mail className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{lead.primary_contact.email}</span>
                          </a>
                          <CopyButton value={lead.primary_contact.email} label="email" />
                        </div>
                      )}
                      {(lead.primary_contact.phone || lead.primary_contact.mobile) && (
                        <div className="flex items-center gap-1 text-sm">
                          <a href={`tel:${lead.primary_contact.phone || lead.primary_contact.mobile}`} className="flex items-center gap-2 flex-1 text-muted-foreground hover:text-primary transition-colors min-w-0">
                            <Phone className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{lead.primary_contact.phone || lead.primary_contact.mobile}</span>
                          </a>
                          <CopyButton value={lead.primary_contact.phone || lead.primary_contact.mobile} label="telefone" />
                        </div>
                      )}
                      {lead.primary_contact.linkedin_url && (
                        <a href={lead.primary_contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#0a66c2] hover:underline">
                          <Linkedin className="h-3.5 w-3.5" /> Perfil LinkedIn
                        </a>
                      )}
                      {lead.primary_contact.whatsapp && (
                        <a href={`https://wa.me/${lead.primary_contact.whatsapp.replace(/\D/g, '')}?text=Ola ${lead.primary_contact.first_name}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-green-600 hover:underline">
                          <Phone className="h-3.5 w-3.5" /> WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="space-y-5">

                {/* Lead Details */}
                <div className="border rounded-lg p-4 space-y-3">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Detalhes do Lead</h3>
                  <div className="space-y-2">
                    <EditableField
                      icon={Target}
                      label="Origem"
                      value={lead.origin}
                      type="select"
                      options={Object.entries(ORIGIN_LABELS).map(([k, v]) => ({ value: k, label: v }))}
                      alwaysShow
                      onSave={async (v) => { await updateLead.mutateAsync({ leadId: lead.id, data: { origin: (v || 'active_prospecting') as any } }) }}
                    />
                    <EditableField
                      icon={Star}
                      label="Sub-Origem"
                      value={lead.sub_origin}
                      type="text"
                      placeholder="ex: LinkedIn, Indicação João, IAM Tech Day..."
                      alwaysShow
                      comboboxOrigin={lead.origin}
                      onSave={async (v) => { await updateLead.mutateAsync({ leadId: lead.id, data: { sub_origin: v } }) }}
                    />
                    {canChangeOwner ? (
                      <EditableField
                        icon={Award}
                        label="Responsável"
                        value={lead.owner_id || ''}
                        type="select"
                        options={ownerOptions}
                        alwaysShow
                        onSave={async (v) => {
                          if (!v) return
                          await updateLead.mutateAsync({ leadId: lead.id, data: { owner_id: v } })
                        }}
                      />
                    ) : (
                      <InfoRow icon={Award} label="Responsável" value={lead.owner_name || '—'} />
                    )}
                    <EditableField
                      icon={FileText}
                      label="Nome do Lead"
                      value={lead.title}
                      type="text"
                      placeholder="ex: Projeto IGA Raizen"
                      alwaysShow
                      onSave={async (v) => { await updateLead.mutateAsync({ leadId: lead.id, data: { title: v || '' } }) }}
                    />
                    {lead.primary_contact_id && contacts.length > 0 && (
                      <EditableField
                        icon={Star}
                        label="Contato Principal"
                        value={lead.primary_contact_id}
                        type="select"
                        options={contacts.map(c => ({ value: c.id, label: c.display_name || c.first_name }))}
                        alwaysShow
                        onSave={async (v) => { await updateLead.mutateAsync({ leadId: lead.id, data: { primary_contact_id: v } }) }}
                      />
                    )}
                    {lead.partner_name && <InfoRow icon={Users} label="Parceiro" value={lead.partner_name} />}
                    {lead.linkedin_profile_url && (
                      <div className="flex items-start gap-3 text-sm">
                        <Linkedin className="h-4 w-4 text-[#0a66c2] mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-muted-foreground text-xs">LinkedIn do Lead</span>
                          <a href={lead.linkedin_profile_url} target="_blank" rel="noopener noreferrer" className="block text-[#0a66c2] hover:underline truncate max-w-[260px]">Ver perfil</a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Score Breakdown */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Composição do Score</h3>
                    <span className="text-xs font-bold">{scoreBreakdown?.total ?? lead.score} / 100</span>
                  </div>
                  {!scoreBreakdown ? (
                    <p className="text-xs text-muted-foreground">Carregando...</p>
                  ) : (
                    <div className="space-y-2.5">
                      {([
                        { key: 'company_completeness', label: 'Empresa (dados completos)', color: '#3b82f6' },
                        { key: 'engagement', label: 'Engajamento (atividades, recência)', color: '#22c55e' },
                        { key: 'bant', label: 'BANT (qualificação)', color: '#f9b233' },
                        { key: 'profile_fit', label: 'Fit (indústria, porte)', color: '#a855f7' },
                      ] as const).map(b => {
                        const item = scoreBreakdown[b.key]
                        const pct = item.max > 0 ? Math.round((item.score / item.max) * 100) : 0
                        return (
                          <div key={b.key}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-muted-foreground">{b.label}</span>
                              <span className="font-semibold">{item.score} / {item.max}</span>
                            </div>
                            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: b.color }} />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Products */}
                {lead.products_interest?.length > 0 && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Produtos de Interesse</h3>
                    <div className="flex flex-wrap gap-2">
                      {lead.products_interest.map(p => <Badge key={p} variant="outline">{p}</Badge>)}
                    </div>
                  </div>
                )}

                {/* Notes (editáveis inline) */}
                <div className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Observacoes</h3>
                    {!editingNotes && (
                      <button
                        onClick={() => { setEditingNotes(true); setNotesDraft(lead.notes || '') }}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                        title={lead.notes ? 'Editar observações' : 'Adicionar observações'}
                      >
                        <Pencil className="h-3 w-3" /> {lead.notes ? 'Editar' : 'Adicionar'}
                      </button>
                    )}
                  </div>
                  {editingNotes ? (
                    <div className="space-y-2">
                      <textarea
                        value={notesDraft}
                        onChange={e => setNotesDraft(e.target.value)}
                        className="w-full text-sm bg-background border rounded-md px-2 py-1.5 min-h-[80px]"
                        placeholder="Adicione observações sobre o lead..."
                        autoFocus
                      />
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingNotes(false); setNotesDraft('') }} disabled={updateLead.isPending}>Cancelar</Button>
                        <Button size="sm" onClick={() => {
                          updateLead.mutate(
                            { leadId: lead.id, data: { notes: notesDraft || null } },
                            { onSuccess: () => { setEditingNotes(false); setNotesDraft('') } },
                          )
                        }} disabled={updateLead.isPending}>
                          {updateLead.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                          Salvar
                        </Button>
                      </div>
                    </div>
                  ) : lead.notes ? (
                    <p className="text-sm whitespace-pre-wrap bg-muted/30 p-3 rounded-lg leading-relaxed">{lead.notes}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">Nenhuma observação cadastrada.</p>
                  )}
                </div>

                {/* Journey */}
                {lead.journey_progress?.length > 0 && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Jornada</h3>
                    {lead.journey_progress.map(jp => (
                      <div key={jp.id} className="flex items-center gap-2 text-sm">
                        <Badge variant={jp.is_active ? 'default' : 'secondary'}>{jp.journey_name}</Badge>
                        <span className="text-muted-foreground">&rarr;</span>
                        <span className="font-medium">{jp.stage_name}</span>
                        <span className="text-xs text-muted-foreground">({jp.days_in_stage}d)</span>
                        {jp.is_sla_breached && <Badge variant="destructive">SLA</Badge>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Metadata */}
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2 border-t">
                  <span>Criado: {formatDate(lead.created_at)}</span>
                  <span>Atualizado: {formatDate(lead.updated_at)}</span>
                  {lead.qualified_at && <span>Qualificado: {formatDate(lead.qualified_at)}</span>}
                </div>
              </div>
            </div>
          )}

          {/* === TAB: Contacts === */}
          {tab === 'contacts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Contatos ({contacts.length})</h3>
                <div className="flex items-center gap-2">
                  <SnovSuggestButton companyId={lead.company_id} onCreated={refetchContacts} />
                  <SnovEnrichButton companyId={lead.company_id} hasWebsite={!!lead.company?.website} onEnriched={refetchContacts} />
                  <Button size="sm" variant="outline" onClick={() => { setEditContact(null); setShowContactForm(true) }}>
                    <UserPlus className="h-4 w-4 mr-1" /> Novo Contato
                  </Button>
                </div>
              </div>

              {contacts.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Nenhum contato cadastrado</p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={() => { setEditContact(null); setShowContactForm(true) }}>
                    <UserPlus className="h-4 w-4 mr-1" /> Cadastrar Contato
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {contacts.map(c => (
                    <div key={c.id} className={`border rounded-lg p-4 hover:border-primary/30 transition-colors ${c.is_primary ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                            {c.first_name?.[0]}{c.last_name?.[0] || ''}
                          </div>
                          <div>
                            <p className="font-semibold text-sm flex items-center gap-1.5">
                              {c.display_name || `${c.first_name} ${c.last_name || ''}`}
                              {c.is_primary && <Star className="h-3 w-3 text-amber-400 fill-amber-400" />}
                            </p>
                            {c.job_title && <p className="text-xs text-muted-foreground">{c.job_title}</p>}
                            {c.role && <Badge variant="outline" className="mt-1 text-[10px]">{CONTACT_ROLE_LABELS[c.role] || c.role}</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditContact(c); setShowContactForm(true) }} title="Editar">
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleteContactConfirm(c)} title="Excluir">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1">
                        {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary"><Mail className="h-3 w-3" /> {c.email}</a>}
                        {(c.phone || c.mobile) && <span className="flex items-center gap-2 text-xs text-muted-foreground"><Phone className="h-3 w-3" /> {c.phone || c.mobile}</span>}
                        {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#0a66c2] hover:underline"><Linkedin className="h-3 w-3" /> LinkedIn</a>}
                        {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-green-600 hover:underline"><Phone className="h-3 w-3" /> WhatsApp</a>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === TAB: BANT === */}
          {tab === 'bant' && (
            <div className="space-y-5 max-w-2xl">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Qualificação BANT — preencha as informações completas (faixa de orçamento, descrição da necessidade, prazo etc).</p>
                <Button size="sm" onClick={() => setQualifyLeadModal(lead)} leftIcon={<ClipboardCheck className="h-4 w-4" />}>
                  Editar Qualificação
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {([
                  { key: 'budget_confirmed' as const, letter: 'B', title: 'Budget (Orçamento)', desc: 'O prospect tem orçamento aprovado para a solução?', detail: lead.budget_range, confirmed: bantState.budget_confirmed },
                  { key: 'authority_confirmed' as const, letter: 'A', title: 'Authority (Autoridade)', desc: 'Estamos falando com o decisor ou influenciador-chave?', detail: null, confirmed: bantState.authority_confirmed },
                  { key: 'need_confirmed' as const, letter: 'N', title: 'Need (Necessidade)', desc: 'Há uma necessidade clara e urgente identificada?', detail: lead.need_description, confirmed: bantState.need_confirmed },
                  { key: 'timeline_confirmed' as const, letter: 'T', title: 'Timeline (Prazo)', desc: 'Existe um prazo definido para decisão/implementação?', detail: lead.timeline_description, confirmed: bantState.timeline_confirmed },
                ]).map(b => (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => setQualifyLeadModal(lead)}
                    className={`text-left border-2 rounded-xl p-5 transition-all hover:shadow-md ${b.confirmed ? 'border-green-500/50 bg-green-500/5 dark:bg-green-900/10' : 'border-border hover:border-primary/30'}`}
                    title="Clique para abrir qualificação completa"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`w-8 h-8 rounded-lg text-sm font-bold inline-flex items-center justify-center ${b.confirmed ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                        {b.letter}
                      </span>
                      <div>
                        <span className="text-sm font-semibold">{b.title}</span>
                        {b.confirmed && <CheckCircle className="h-4 w-4 text-green-500 inline ml-2" />}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{b.desc}</p>
                    {b.detail && <p className="text-xs mt-2 px-2 py-1 bg-muted/50 rounded font-medium">{b.detail}</p>}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3 pt-4 border-t">
                <span className="text-sm text-muted-foreground">Score BANT:</span>
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className={`w-8 h-2 rounded-full ${i < bantScoreLocal ? 'bg-green-500' : 'bg-muted'}`} />
                  ))}
                </div>
                <span className="text-sm font-bold">{bantScoreLocal}/4</span>
              </div>
            </div>
          )}

          {/* === TAB: OPORTUNIDADES VINCULADAS === */}
          {tab === 'opportunities' && (
            <div className="max-w-4xl space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {isLoadingLeadOpps
                    ? 'Carregando oportunidades...'
                    : `${linkedOpenOpps.length} oportunidade${linkedOpenOpps.length === 1 ? '' : 's'} aberta${linkedOpenOpps.length === 1 ? '' : 's'} para este lead`}
                </p>
                <Button variant="outline" size="sm" onClick={() => setShowOppForm(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Nova
                </Button>
              </div>

              {isLoadingLeadOpps ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : linkedOpenOpps.length === 0 ? (
                <div className="border border-dashed rounded-lg p-8 text-center">
                  <TrendingUp className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Nenhuma oportunidade aberta vinculada a este lead.</p>
                  <Button variant="link" size="sm" className="mt-2" onClick={() => setShowOppForm(true)}>
                    Criar oportunidade
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {linkedOpenOpps.map((opp) => {
                    const stageCfg = ({
                      qualified: { label: 'Qualificado', variant: 'success' as const },
                      proposal_sent: { label: 'Proposta Enviada', variant: 'warning' as const },
                      negotiation: { label: 'Negociacao', variant: 'info' as const },
                      closing: { label: 'Fechamento', variant: 'default' as const },
                    } as const)[opp.stage as 'qualified' | 'proposal_sent' | 'negotiation' | 'closing'] || { label: opp.stage, variant: 'secondary' as const }
                    const valueFmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(opp.value || 0)
                    return (
                      <a
                        key={opp.id}
                        href={`/opportunities/${opp.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block border rounded-lg p-4 hover:border-primary hover:bg-accent/40 transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1.5">
                              <h4 className="font-semibold text-sm truncate group-hover:text-primary">{opp.name}</h4>
                              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                              <Badge variant={stageCfg.variant}>{stageCfg.label}</Badge>
                              <span className="font-medium text-foreground">{valueFmt}</span>
                              <span>{opp.probability}% prob.</span>
                              {opp.owner_name && (
                                <span className="flex items-center gap-1">
                                  <Award className="h-3 w-3" /> {opp.owner_name}
                                </span>
                              )}
                              {opp.expected_close_date && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" /> {formatDate(opp.expected_close_date)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </a>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* === TAB: TAREFAS === */}
          {tab === 'tasks' && (
            <div className="max-w-3xl">
              <TasksPanel leadId={lead.id} />
            </div>
          )}

          {/* === TAB: HISTORICO === */}
          {tab === 'history' && (
            <div className="max-w-3xl">
              <LeadTimeline lead={lead} />
            </div>
          )}
        </div>
      </div>

      {/* Contact Form Sub-Modal */}
      {showContactForm && lead.company_id && (
        <ContactFormModal
          contact={editContact}
          companyId={lead.company_id}
          onClose={() => { setShowContactForm(false); setEditContact(null) }}
          onSaved={() => { refetchContacts(); setShowContactForm(false); setEditContact(null) }}
        />
      )}

      <ConfirmDialog
        open={!!deleteContactConfirm}
        title="Excluir contato?"
        message={`Tem certeza que deseja excluir "${deleteContactConfirm?.display_name || deleteContactConfirm?.first_name || ''}"?`}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="destructive"
        isLoading={deleteContactMut.isPending}
        onConfirm={async () => {
          if (!deleteContactConfirm) return
          try {
            await deleteContactMut.mutateAsync(deleteContactConfirm.id)
            await refetchContacts()
          } catch { /* ignore */ }
          setDeleteContactConfirm(null)
        }}
        onCancel={() => setDeleteContactConfirm(null)}
      />

      {/* Qualify BANT Modal (canônico) */}
      {qualifyLeadModal && (
        <QualifyBantModal lead={qualifyLeadModal} onClose={() => setQualifyLeadModal(null)} />
      )}

      {/* Create Opportunity Modal */}
      {showOppForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => setShowOppForm(false)}>
          <div className="bg-background rounded-xl shadow-xl max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Nova Oportunidade</h2>
              <button onClick={() => setShowOppForm(false)} className="p-1 rounded hover:bg-muted"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (!oppForm.name) return; createOpp.mutate({ lead_id: lead.id, name: oppForm.name, value: oppForm.value, probability: oppForm.probability, expected_close_date: oppForm.expected_close_date || null } as OpportunityCreate, { onSuccess: (opp) => { setShowOppForm(false); onClose(); navigate(`/opportunities/${opp.id}`) } }) }} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Lead / Empresa</label>
                <div className="mt-1 px-3 py-2 rounded-md border bg-muted/50 text-sm">{lead.title || lead.company?.trading_name || lead.company?.name || '-'}</div>
              </div>
              <div>
                <label className="text-sm font-medium">Nome da Oportunidade *</label>
                <Input className="mt-1" required value={oppForm.name} onChange={e => setOppForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Implementacao IAM Empresa X" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Valor Estimado (R$)</label>
                  <Input className="mt-1" type="number" min={0} step={100} value={oppForm.value || ""} onChange={e => setOppForm(f => ({ ...f, value: Number(e.target.value) }))} />
                </div>
                <div>
                  <label className="text-sm font-medium">Probabilidade (%)</label>
                  <Input className="mt-1" type="number" min={0} max={100} value={oppForm.probability || ""} onChange={e => setOppForm(f => ({ ...f, probability: Number(e.target.value) }))} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Data Prevista de Fechamento</label>
                <Input className="mt-1" type="date" value={oppForm.expected_close_date || ""} onChange={e => setOppForm(f => ({ ...f, expected_close_date: e.target.value }))} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowOppForm(false)}>Cancelar</Button>
                <Button type="submit" disabled={createOpp.isPending || !oppForm.name}>
                  {createOpp.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Criar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// ==========================================
// EditableField — linha de info clicável, edita inline (text ou select)
// ==========================================

function EditableField({
  icon: Icon,
  label,
  value,
  type = 'text',
  options,
  placeholder,
  alwaysShow,
  onSave,
  comboboxOrigin,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string | null | undefined
  type?: 'text' | 'select'
  options?: { value: string; label: string }[]
  placeholder?: string
  alwaysShow?: boolean
  onSave: (newValue: string | null) => Promise<void>
  // Quando setado, modo 'text' usa SubOriginCombobox em vez de Input puro,
  // mostrando dropdown de sub-origens já cadastradas (filtradas pela origin passada)
  comboboxOrigin?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  if (!value && !alwaysShow && !editing) return null

  const start = () => {
    setDraft(value || '')
    setError('')
    setEditing(true)
  }
  const cancel = () => { setEditing(false); setError('') }
  const save = async () => {
    setSaving(true); setError('')
    try {
      const trimmed = draft.trim()
      await onSave(trimmed || null)
      setEditing(false)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const displayValue = value || (alwaysShow ? <span className="text-muted-foreground italic font-normal">— Clique para preencher —</span> : null)
  const displayLabel = type === 'select' && value && options ? (options.find(o => o.value === value)?.label || value) : value

  return (
    <div className="flex items-start gap-3 text-sm group">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-muted-foreground text-xs block">{label}</span>
        {editing ? (
          <div className="mt-1 space-y-1">
            <div className="flex items-center gap-1">
              {type === 'select' ? (
                <select
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="flex h-8 rounded-md border border-input bg-background px-2 text-sm flex-1 min-w-0"
                  disabled={saving}
                >
                  <option value="">— vazio —</option>
                  {options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : comboboxOrigin !== undefined ? (
                <div className="flex-1 min-w-0">
                  <SubOriginCombobox
                    autoFocus
                    origin={comboboxOrigin}
                    value={draft}
                    onChange={setDraft}
                    onEnter={save}
                    onEscape={cancel}
                    placeholder={placeholder}
                    disabled={saving}
                    inputClassName="h-8 text-sm"
                  />
                </div>
              ) : (
                <Input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); save() }
                    if (e.key === 'Escape') { e.preventDefault(); cancel() }
                  }}
                  placeholder={placeholder}
                  className="h-8 text-sm"
                  disabled={saving}
                />
              )}
              <Button size="sm" onClick={save} disabled={saving}>{saving ? '...' : 'Salvar'}</Button>
              <Button size="sm" variant="ghost" onClick={cancel} disabled={saving}><X className="h-3.5 w-3.5" /></Button>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        ) : (
          <button onClick={start} className="text-left max-w-full inline-flex items-center gap-2 hover:text-primary transition-colors">
            <span className="font-medium truncate">{displayLabel || displayValue}</span>
            <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </button>
        )}
      </div>
    </div>
  )
}


// Combobox para campo Sub-Origem: Input livre + dropdown de sugestões já usadas
// no banco (filtradas por origem), ordenadas por frequência. Usuário pode digitar
// valor novo livremente — sugestões são apenas autocomplete, não restrição.
function SubOriginCombobox({
  origin,
  value,
  onChange,
  placeholder,
  autoFocus,
  onEnter,
  onEscape,
  disabled,
  inputClassName,
}: {
  origin?: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  autoFocus?: boolean
  onEnter?: () => void
  onEscape?: () => void
  disabled?: boolean
  inputClassName?: string
}) {
  const { data: suggestions = [] } = useSubOrigins(origin)
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const trimmed = value.trim()
  const filtered = useMemo(() => {
    if (!trimmed) return suggestions
    const q = trimmed.toLowerCase()
    return suggestions.filter(s => s.value.toLowerCase().includes(q))
  }, [suggestions, trimmed])

  const isNew = trimmed.length > 0 && !suggestions.some(s => s.value.toLowerCase() === trimmed.toLowerCase())

  function renderHighlighted(text: string) {
    if (!trimmed) return text
    const idx = text.toLowerCase().indexOf(trimmed.toLowerCase())
    if (idx < 0) return text
    return (
      <>
        {text.slice(0, idx)}
        <span className="font-semibold text-primary">{text.slice(idx, idx + trimmed.length)}</span>
        {text.slice(idx + trimmed.length)}
      </>
    )
  }

  return (
    <div ref={wrapRef} className="relative">
      <Input
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); setOpen(false); onEnter?.() }
          if (e.key === 'Escape') { e.preventDefault(); setOpen(false); onEscape?.() }
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={inputClassName}
      />
      {open && (filtered.length > 0 || isNew) && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border bg-popover shadow-md">
          {filtered.map(s => (
            <button
              key={s.value}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(s.value); setOpen(false) }}
              className="flex items-center justify-between gap-2 w-full px-3 py-1.5 text-sm hover:bg-muted text-left"
            >
              <span className="truncate">{renderHighlighted(s.value)}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground flex-shrink-0">×{s.count}</span>
            </button>
          ))}
          {isNew && (
            <div className="px-3 py-1.5 text-xs text-muted-foreground border-t bg-muted/30">
              <span className="italic">"{trimmed}"</span> será criada como nova sub-origem
            </div>
          )}
        </div>
      )}
    </div>
  )
}


function InfoRow({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div>
        <span className="text-muted-foreground text-xs">{label}</span>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  )
}

// ==========================================
// Snov.io Enrichment Button
// ==========================================

function SnovEnrichButton({ companyId, hasWebsite, onEnriched }: { companyId: string; hasWebsite: boolean; onEnriched?: () => void }) {
  const enrichMut = useSnovEnrichCompany()
  const [result, setResult] = useState<{ ok: boolean; message: string; details?: any[] } | null>(null)

  const handleClick = async () => {
    if (!companyId) return
    setResult(null)
    try {
      const data = await enrichMut.mutateAsync(companyId)
      const msg = data.contacts_enriched > 0
        ? `${data.contacts_enriched} contato(s) enriquecido(s) com email. Domínio ${data.domain} tem ${data.domain_emails_in_snov} emails na base Snov. Créditos restantes: ${data.credits_balance ?? '—'}`
        : `Nenhum email encontrado para os contatos atuais. Domínio ${data.domain} tem ${data.domain_emails_in_snov} emails na base Snov. Créditos restantes: ${data.credits_balance ?? '—'}`
      setResult({ ok: true, message: msg, details: data.details })
      onEnriched?.()
    } catch (e: any) {
      setResult({ ok: false, message: e?.response?.data?.detail || 'Erro ao enriquecer com Snov.io' })
    }
  }

  return (
    <div className="relative">
      <Button
        size="sm"
        variant="outline"
        onClick={handleClick}
        disabled={enrichMut.isPending}
        title="Buscar emails dos contatos via Snov.io"
      >
        <Search className="h-4 w-4 mr-1" />
        {enrichMut.isPending ? 'Enriquecendo...' : 'Enriquecer com Snovio'}
      </Button>
      {result && (
        <div className={`absolute right-0 top-full mt-2 w-96 z-50 p-3 rounded-lg shadow-lg border text-xs ${result.ok ? 'bg-green-50 border-green-300 dark:bg-green-950/40 dark:border-green-700' : 'bg-destructive/10 border-destructive/40'}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="font-semibold">{result.ok ? '✓ Snov.io' : '✗ Snov.io'}</span>
            <button onClick={() => setResult(null)} className="text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
          </div>
          <p className="leading-relaxed">{result.message}</p>
          {result.details && result.details.length > 0 && (
            <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
              {result.details.map((d, i) => (
                <li key={i} className="font-mono text-[11px]">{d.name} → {d.email}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

// ==========================================
// Snov Suggest Button — descobre nomes via IA + valida emails via Snov
// ==========================================

function SnovSuggestButton({ companyId, onCreated }: { companyId: string; onCreated?: () => void }) {
  const suggestMut = useSnovSuggestContacts()
  const bulkCreateMut = useBulkCreateContacts()
  const [suggestions, setSuggestions] = useState<SnovSuggestion[] | null>(null)
  // Chave de seleção: email se houver, senão "name|first|last|role"
  const keyOf = (s: SnovSuggestion) => s.email || `name|${s.first_name}|${s.last_name}|${s.role}`
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [error, setError] = useState('')
  const [feedback, setFeedback] = useState('')

  const openModal = async () => {
    if (!companyId) return
    setError(''); setFeedback(''); setSelectedKeys(new Set())
    setSuggestions([])
    try {
      const res = await suggestMut.mutateAsync(companyId)
      setSuggestions(res.suggestions || [])
      // Pré-seleciona os que têm email confirmado E não existem
      const pre = new Set((res.suggestions || []).filter(s => !s.already_exists && s.email).map(keyOf))
      setSelectedKeys(pre)
      if (!res.suggestions || res.suggestions.length === 0) {
        setFeedback(res.message || `Nenhum candidato encontrado para ${res.domain}.`)
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao buscar sugestões')
      setSuggestions(null)
    }
  }

  const close = () => { setSuggestions(null); setError(''); setFeedback(''); setSelectedKeys(new Set()) }

  const toggle = (s: SnovSuggestion) => {
    const k = keyOf(s)
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k); else next.add(k)
      return next
    })
  }

  const cadastrar = async () => {
    if (!suggestions || selectedKeys.size === 0) return
    const toCreate = suggestions
      .filter(s => selectedKeys.has(keyOf(s)) && !s.already_exists)
      .map(s => ({ first_name: s.first_name, last_name: s.last_name, email: s.email, job_title: s.role }))
    if (toCreate.length === 0) { setFeedback('Nada novo pra cadastrar — selecionados já existem.'); return }
    try {
      const result = await bulkCreateMut.mutateAsync({ companyId, contacts: toCreate })
      setFeedback(`✓ ${result.created} contato(s) criado(s)${result.skipped_duplicates > 0 ? `, ${result.skipped_duplicates} duplicado(s) ignorado(s)` : ''}.`)
      onCreated?.()
      setTimeout(close, 1500)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao criar contatos')
    }
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={openModal} disabled={suggestMut.isPending} title="Sugerir contatos do ICP via IA + Snov">
        <Search className="h-4 w-4 mr-1" />
        {suggestMut.isPending ? 'Buscando...' : 'Sugerir Contatos'}
      </Button>

      {(suggestions !== null || suggestMut.isPending) && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={close}>
          <div className="bg-card border rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <div>
                <h2 className="font-semibold">Sugestões de Contatos do ICP</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Diretores, Gestores, C-Level, Compliance, Segurança, IAM, GRC</p>
              </div>
              <button onClick={close} className="p-1.5 rounded hover:bg-accent"><X className="h-4 w-4" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {suggestMut.isPending ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Pesquisando candidatos via IA + validando emails na Snov...
                  <p className="text-xs mt-2">(pode levar 10-30s)</p>
                </div>
              ) : error ? (
                <p className="text-sm text-destructive">{error}</p>
              ) : suggestions && suggestions.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground mb-2">
                    Candidatos do ICP. <span className="text-green-600 dark:text-green-400 font-semibold">Verde</span> = email confirmado pela Snov; <span className="text-amber-600 dark:text-amber-400 font-semibold">Amarelo</span> = sem email validado (cadastra só nome/cargo, preencha email depois).
                  </p>
                  {suggestions.map((s, i) => {
                    const isSelected = selectedKeys.has(keyOf(s))
                    const hasEmail = !!s.email
                    return (
                      <label
                        key={i}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                          s.already_exists ? 'bg-muted/40 opacity-60' :
                          hasEmail ? 'border-green-500/30 hover:bg-green-50/30 dark:hover:bg-green-950/20' :
                          'border-amber-500/30 hover:bg-amber-50/30 dark:hover:bg-amber-950/20'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggle(s)}
                          disabled={s.already_exists}
                          className="h-4 w-4"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{s.first_name} {s.last_name}</div>
                          <div className="text-xs text-muted-foreground truncate">{s.role}</div>
                          <div className={`text-xs font-mono mt-0.5 ${hasEmail ? '' : 'italic text-muted-foreground'}`}>
                            {hasEmail ? s.email : '— sem email validado pela Snov —'}
                          </div>
                        </div>
                        {s.already_exists && <Badge variant="outline" className="text-[9px]">Já cadastrado</Badge>}
                        {!s.already_exists && hasEmail && <Badge variant="outline" className="text-[9px] border-green-500/40 text-green-600 dark:text-green-400">Snov ✓</Badge>}
                        {!s.already_exists && !hasEmail && <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-600 dark:text-amber-400">só IA</Badge>}
                      </label>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">{feedback || 'Nenhuma sugestão encontrada.'}</p>
              )}
            </div>

            {suggestions && suggestions.length > 0 && (
              <div className="px-5 py-3 border-t flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{selectedKeys.size} selecionado(s)</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={close}>Cancelar</Button>
                  <Button size="sm" onClick={cadastrar} disabled={bulkCreateMut.isPending || selectedKeys.size === 0}>
                    {bulkCreateMut.isPending ? 'Cadastrando...' : `Cadastrar ${selectedKeys.size}`}
                  </Button>
                </div>
              </div>
            )}
            {feedback && <p className="px-5 py-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 border-t">{feedback}</p>}
          </div>
        </div>
      )}
    </>
  )
}

// ==========================================
// Delete Lead Modal
// ==========================================

function DeleteLeadModal({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const mutation = useDeleteLead()
  const [error, setError] = useState('')

  const handleDelete = async () => {
    try { await mutation.mutateAsync(lead.id); onClose() }
    catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao excluir lead'))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Excluir Lead</h2>
          <p className="text-sm text-muted-foreground mb-4">Excluir permanentemente o lead <strong>{lead.company?.name || 'esta empresa'}</strong>?</p>
          <p className="text-xs text-muted-foreground mb-4">Histórico de outreach (e-mails, mensagens) e contatos da empresa são preservados. Oportunidades em andamento bloqueiam a exclusão.</p>
          {error && <div className="p-3 rounded bg-destructive/10 text-destructive text-sm mb-4">{error}</div>}
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} isLoading={mutation.isPending}>Excluir</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
